import store from '@/store/store';
import {isFinalizedInvoice, isTaskTimerLockedByFinalizedInvoice} from './Invoices/finalizedLock';
import Transaction, {SourceTypeType} from "@/entities/transaction";
import util from "@/lib/util";
import {captureException} from "@/lib/picos/error";
import {getCost} from '@/TaskManagement/TaskHelper';
import * as DateUtil from "@/lib/dateutils";
import {dateAsNumber, isBetween} from "@/lib/dateutils";
import {firmCalendarDay, firmEndOfDay, firmStartOfDay, firmTimeZone, isOnOrAfterFirmDay, isOnOrBeforeFirmDay} from "@/lib/firmTime";
import CaseHelper from "@/Case/CaseHelper";
import IconButton from "@/ui/IconButton/IconButton";
import Icons, {IconType} from "@/ui/IconButton/Icons";
import MathUtils from "@/lib/mathUtils";
import {hashIds} from '@/lib/memoize';
import InvoiceHelper from './Invoices/InvoiceHelper';
import Matter from '@/entities/matter';
import i18n from '@/i18n/i18n';
import {RestoreStatus} from '@/store/actions/miscActions';
import {Tooltip} from "@mui/material";
import {isBalanceAdjustment, isEscrow, isPayment, isTransfer, isWithdrawal} from "./Transactions/TransactionHelper";
import {formatFML, formatLFM} from "@/Contacts/ContactFormat";
import find from "@/store/find";
import {getZeroUUID} from "@/lib/picos/uuids";
import newHRTimestamp from "@/lib/picos/timestamp";
import {format, isSameDay} from "date-fns";
import Lead from "@/entities/lead";
import {toTwoDecimals} from "@/lib/picos/numberFormat";
import type {uuid} from "@/@types/schema";
import Invoice, {InvoiceStatus} from "@/entities/invoice";
import Task from "@/entities/task";
import Expense from "@/entities/expense";
import Discount from "@/entities/discount";
import FlatFee from "@/entities/flatFee";
import FlatFeeMilestone from "@/entities/flatFeeMilestone";
import BankAccount from "@/entities/bankAccount";
import {ReactElement} from "react";
import {existsAndNotDeletedFilter} from "@/lib/picos/filters";
import CopyText from "@/ui/CopyText";
import Contact from "@/entities/contact";
import type {SelectOption} from "@/@types/select";
import type {FinalTrustInfo, FinalValues} from "@/@types/invoice";
import {isPortalUser} from "@/lib/picos/currentLogin";
import OptionHelper from "@/Configuration/GeneralOptions/OptionHelper";
import { InterestMethods } from "@/Configuration/GeneralOptions/OptionConstants";
import { calculateTaxTotal } from "./Invoices/calculateTax";
import { calculateInterest } from "./calculateInterest";
import {moneyCheckedAdd, moneyCheckedSubtract, moneyCheckedSum} from '@/lib/payments';

const ZERO_DATE = new Date(0);

export enum NegativeTransactionType {
    DE = 'DE',
    RR = 'RR',
    DT = 'DT',
    PT = 'PT',
    PC = 'PC',
    BA = 'BA'
}

export type NegativeTransactionTypes = keyof typeof NegativeTransactionType;

export enum NotTrustTransactionType {
    BA = 'BA',
    SA = 'SA',
    PC = 'PC',
    RO = 'RO'
}

export type NotTrustTransactionTypes = keyof typeof NotTrustTransactionType;

export enum StarterTransactionType {
    ST = 'ST',
    SA = 'SA'
}

export type StarterTransactionTypes = keyof typeof StarterTransactionType;


/**
 * Used by {@link #getInvoiceInfo}
 */
export type InvoiceInfo = {
    /** UUID of the invoice */
    invoiceId: uuid;

    /** The full Invoice entity */
    invoice: Invoice;

    /** Billable tasks included in this invoice (excludes hidden, no-charge, and flat-fee-bucket tasks) */
    tasks: Task[];

    /** Reimbursable expenses included in this invoice */
    expenses: Expense[];

    /** All transactions related to this invoice, filtered by matter/lead */
    transactions: Transaction[];

    /** Sum of billable task costs (excluding flat-fee-bucket, hidden, and no-charge tasks) */
    taskTotal: number;

    /** Sum of expense.quantity * expense.cost for all included expenses */
    expenseTotal: number;

    /** Sum of all transactions with sign handling (uses paymentDistribution when available) */
    transactionTotal: number;

    /** Discounts applied to this invoice (percentage or flat, on labor/expenses/both) */
    discounts: Discount[];

    /** Sum of all discount amounts */
    discountTotal: number;

    /** Flat fee details: earnedAmount (total earned this period), flatFees (non-milestone), flatFeeMilestones (milestone-based) */
    flatFeeInfo: {
        /** Total earned this period from flat fees */
        earnedAmount: number;

        /** All non-milestone flat fees for this case */
        flatFees: FlatFee[];

        /** All milestone flat fees for this case */
        flatFeeMilestones: FlatFeeMilestone[];
    };

    /** Total earned from flat fees (equals flatFeeInfo.earnedAmount) */
    flatFeeTotal: number;

    /** Payment transactions (types PC, PT, FO, and TT with sourceType 'B'; excludes BA and DT) */
    payments: Transaction[];

    /** Sum of payment amounts (TT transactions are subtracted) */
    paymentTotal: number;

    /** Balance adjustment transactions (txType 'BA') */
    adjustments: Transaction[];

    /** Sum of balance adjustment amounts */
    adjustmentTotal: number;

    /** All 'RO' transactions where money was refunded from operating */
    refunds: Transaction[];

    /** Sum of 'RO' transactions where money was refunded from operating */
    refundTotal: number;

    /** taskTotal + expenseTotal + flatFeeTotal - discountTotal - adjustmentTotal + refundTotal */
    currentInvoiceAmount: number;

    /** pastDue + currentInvoiceAmount */
    currentBalance: number;

    /** currentBalance - paymentTotal */
    amountDue: number;

    /** Sum of all previous finalized invoices' amounts minus their payments */
    pastDue: number;

    /** Whether this is the first invoice for these clients/matters */
    isFirstInvoice: boolean;

    /** Tax on taxable expenses: sum of (expense.cost * expense.quantity * taxRate/100) */
    taxTotal: number;

    /** Interest on overdue principal balance from prior finalized invoices */
    interestTotal: number;

    /** Tax label configured at the time (e.g., "Sales Tax", "VAT") */
    taxLabel: string;

    /** Sum of trust withdrawal transactions (txType 'DT') */
    withdrawalsTotal: number;

    /** Trust withdrawal transactions (txType 'DT') */
    withdrawals: Transaction[];
};

/**
 * Used by {@link #getBilledAndPaidForMatters}
 *
 * Note that 'available' is ephemeral and used only for calculation.
 * Do not use unless you know what you are doing.
 */
export type BilledAndPaidInvoice = {
    invoiceNumber: string;
    invoiceId: uuid;
    startDate: Date | null | undefined;
    endDate: Date;
    billed: number;
    collected: number;
    expensesTotal: number;
    paid: boolean;
    finalized: boolean;
    taskIds: uuid[];
    discounts: Discount[];
    available?: number;
};

/** A current receivable, separate from the immutable invoice sent to the client. */
export type InvoiceMoneyBalance = {
    invoiceInfo: InvoiceInfo;
    principal: number;
    /** Invoice discount already deducted from principal; informational, not another settlement. */
    discount: number;
    tax: number;
    interest: number;
    billed: number;
    cash: number;
    credit: number;
    undated: number;
    outstanding: number;
};

export type CashAllocationEvent = {
    transactionId: uuid;
    /** Null means the source has no receipt date; never substitute an invoice date. */
    txDate: Date | null;
    amount: number;
    /** Null invoiceId identifies settlement of an imported opening receivable. */
    allocations: {invoiceId: uuid | null; amount: number}[];
    unapplied: number;
};

export type MoneyAllocation = {
    invoices: InvoiceMoneyBalance[];
    cashEvents: CashAllocationEvent[];
    credits: {transactionId: uuid | null; txDate: Date | null; amount: number}[];
    startingBilled: number;
    /** Imported/unitemized settlement cannot be presented as dated cash receipts. */
    undatedSettled: number;
    finalizedBilled: number;
    totalBilled: number;
    totalSettled: number;
    /** Signed case balance, including excess credit or an unmatched refund. */
    outstanding: number;
    unappliedCash: number;
};

/**
 * Used by getBilledAndCollected
 */
export type UserInfo = {
    userId: uuid;
    name: string;
    roles: string[];
    billableHoursTarget: number;
    billableHoursUnit: string; // "day" | "week" | "month" | "year";
    totalBilledHours: number;
    totalCollectedHours: number;
    totalCollectedByTrustHours: number;
    totalBilled: number;
    totalCollectedThroughInvoicing: number;
    totalCollectedThroughTrusts: number;
    totalUncollected: number;
    tasks: Task[];
    enabled?: boolean;
};

export type TaskInfo = {
    completionDate: Date,
    hoursPaidFromDirect: number,
    hoursPaidFromTrust: number,
    hoursRemaining: number,
    id: uuid,
    paidFromDirect: number,
    paidFromTrust: number,
    remaining: number,
}

export type MatterInfo = {
    matterId: uuid;
    name: string;
    clientNames: string;
    totalBilledHours: number;
    totalCollectedHours: number;
    totalCollectedByTrustHours: number;
    totalBilled: number;
    totalCollectedThroughInvoicing: number;
    totalCollectedThroughTrusts: number;
    totalUncollected: number;
    tasks: TaskInfo[];
};

export type ClientTrustInfo = {
    startingBalance: number,
    endingBalance: number,
    previousTransactions: Transaction[],
    transactionsThisPeriod: Transaction[],
}

export type MatterTrustInfo = {
    startingBalance: number,
    endingBalance: number,
    transactionsThisPeriod: Transaction[],
}

export type TrustAccountSelectOption = SelectOption<
    uuid,
    {
        id: uuid,
        type: string,
        name: string | ReactElement,
        balance: number
    }
>;


export type RedRubberBandInfo = {
    trust: number;
    warningColor: string;
    unbilled: number;
    isWarning: boolean;
    due: number;
    isRedRubberBanded: boolean;
    isRestricted: boolean;
}

export type TrustAmounts = {
    caseTrust: number,
    generalTrust: number,
    totalTrust: number
}

export type InvoiceDateRange = {
    finalized: boolean, startDate: Date | null | undefined, endDate: Date, invoice: Invoice
}

function _generateTransactionSkeleton(obj: Partial<Transaction>) {
    return new Transaction({
        transactionId: util.genUUID(),
        status: 'C',
        addedOn: newHRTimestamp(),
        entityType: 'transaction',
        entityId: getZeroUUID(),
        ...obj
    });
}

// Cache entries for per-invoice granular caching
type InvoiceInfoCacheEntry = { result: InvoiceInfo | null; depsHash: string };
type ClientTrustCacheEntry = { result: ClientTrustInfo | null; depsHash: string };
type MatterTrustCacheEntry = { result: MatterTrustInfo; depsHash: string };
type TransactionsCacheEntry = { resultIds: string[]; depsHash: string };
type ExpensesCacheEntry = { resultIds: string[]; depsHash: string };
type TrustAmountsCacheEntry = { result: TrustAmounts; depsHash: string };

// Cache maps - these persist across renders and only invalidate when dependencies change
const invoiceInfoCache = new Map<string, InvoiceInfoCacheEntry>();
const clientTrustCache = new Map<string, ClientTrustCacheEntry>();
const matterTrustCache = new Map<string, MatterTrustCacheEntry>();
const transactionsCache = new Map<string, TransactionsCacheEntry>();
const expensesCache = new Map<string, ExpensesCacheEntry>();
const trustAmountsCache = new Map<string, TrustAmountsCacheEntry>();

/**
 * Compute a dependency hash for finalized invoices.
 * Only hash the specific entities listed on the invoice.
 * Finalized invoices have fixed entity IDs that don't change.
 */
function computeFinalizedInvoiceDepsHash(invoice: Invoice): string {
    const discounts = store.getState().discounts;

    // Get discounts for this invoice (discounts use entityId to reference the invoice)
    const invoiceDiscountIds = discounts
        .filter(d => d.entityId === invoice.id && d.entityType === 'invoice' && d.status !== 'D')
        .map(d => d.id);

    return hashIds([
        invoice.id,
        ...(invoice.taskIds || []),
        ...(invoice.expenseIds || []),
        ...(invoice.transactionIds || []),
        ...(invoice.flatFeeIds || []),
        ...(invoice.flatFeeMilestoneIds || []),
        ...invoiceDiscountIds,
    ]);
}

/**
 * Compute a dependency hash for draft invoices.
 * Hash all uninvoiced items for the invoice's matters.
 *
 * PERFORMANCE: Uses ArrayMap's getMappingsFor() to leverage indexed lookups
 * instead of filtering through entire arrays. This reduces O(n) scans to O(1) lookups.
 */
/**
 * A transaction's identity plus the fields that change its effect (OWA-946). An id
 * alone missed voids, refusals and edits to an existing transaction.
 */
const txSignal = (tx: Transaction): string =>
    `${tx.id}:${tx.status}:${tx.txType}:${tx.amount}:${tx.txDate?.getTime?.() ?? ''}:` +
    `${tx.voidedDate?.getTime?.() ?? ''}:${tx.refusedDate?.getTime?.() ?? ''}:` +
    `${tx.sourceType}:${tx.source}:${tx.targetType}:${tx.target}:${JSON.stringify(tx.paymentDistribution ?? null)}:` +
    // The invoice it belongs to (trust calculations split on it) and its cases (the
    // draft's selection matches them exactly).
    `${tx.invoiceId ?? ''}:${(tx.matterIds ?? []).join(',')}`;

/** An invoice's identity plus the fields the trust calculations pick earlier invoices by (OWA-946):
 *  its dates, and the clients and matters isFirstInvoice and the previous-invoice choice compare.
 *  Matter order is kept: the client trust calculation picks the previous invoice by matterIds[0]. */
const invoiceSignal = (inv: Invoice): string =>
    `${inv.id}:${inv.status}:${inv.invoiceStatus}:${inv.finalizedOn?.getTime?.() ?? ''}:${inv.endDate?.getTime?.() ?? ''}:${inv.issueDate?.getTime?.() ?? ''}:` +
    `${[...(inv.clientIds ?? [])].sort().join(',')}:${(inv.matterIds ?? []).join(',')}`;

function computeDraftInvoiceDepsHash(invoice: Invoice): string {
    const state = store.getState();

    // Pre-fetch indexed subsets for this invoice's matters/clients
    // These are O(1) lookups using ArrayMap's internal indexes
    // OWA-940: a lead draft has matterIds [] and selects its tasks by leadId and its
    // payments by entityType 'lead' / entityId (_computeTasksForInvoice /
    // _computeTransactionsForInvoice). Without these the matter-keyed lookups never
    // see them, and a new consult payment or task left the cached InvoiceInfo stale.
    const leadTasks = invoice.leadId ? state.tasks.getMappingFor('leadId', invoice.leadId) : [];
    const leadTransactions = invoice.leadId
        ? state.transactions.getIndex('notTrustTypes')
            .filter(tx => tx.entityType === 'lead' && tx.entityId === invoice.leadId)
        : [];
    const matterTasks = [...state.tasks.getMappingsFor('matterId', invoice.matterIds), ...leadTasks];
    const matterExpenses = state.expenses.getMappingsFor('matterId', invoice.matterIds);
    // OWA-1711: the matterId map keys a transaction under every Case it names, so one that names two of
    // this invoice's Cases comes back twice. Count it once.
    const matterTransactions = [...new Map([...state.transactions.getMappingsFor('matterId', invoice.matterIds), ...leadTransactions].map(tx => [tx.id, tx])).values()];
    const matterFlatFees = state.flatFees.getMappingsFor('matterId', invoice.matterIds);
    const matterInvoices = state.invoices.getMappingsFor('matterId', invoice.matterIds);

    // 1. Tasks - uninvoiced for these matters
    // OWA-941: each task's revision too, so editing one (its completion date, time or
    // charge) recomputes the draft; every task save stamps a new addedOn.
    const uninvoicedTaskIds = matterTasks
        .filter(t => (!t.invoiceId || t.invoiceId === invoice.id) && t.billable && !!t.completionDate)
        .map(t => `${t.id}:${t.addedOn}`);

    // 2. Expenses - uninvoiced for these matters
    // OWA-985: each expense's revision too, as for tasks, so editing one (its type, date or
    // amount) recomputes the draft and its expected-expense warnings.
    const uninvoicedExpenseIds = matterExpenses
        .filter(e => (!e.invoiceId || e.invoiceId === invoice.id) && e.reimbursable)
        .map(e => `${e.id}:${e.addedOn}`);

    // 3. Current transactions and prior receipts/credits read by draft past due.
    // Note: client-based lookups still require filter since no clientId mapping exists
    const uninvoicedTxIds = matterTransactions
        .filter(tx => !tx.invoiceId || tx.invoiceId === invoice.id || ['PC', 'PT', 'BA', 'RO'].includes(tx.txType))
        .map(txSignal);
    // OWA-946: client trust deposits reach the draft through getClientTrustInfo, so the
    // draft depends on each client's trust key.
    const clientTrustSignals = (invoice.clientIds ?? []).map(cid => computeClientTrustDepsHash(cid, invoice));
    // Likewise matter trust activity, through getMatterTrustInfo.
    const matterTrustSignals = invoice.matterIds.map(mid => computeMatterTrustDepsHash(mid, invoice));

    // 4. Discounts - use getMappingFor for invoice-level discount, then matter-level
    const invoiceDiscounts = state.discounts.getMappingFor('invoiceId', invoice.id);
    const matterDiscountIds = invoice.matterIds.flatMap(mid =>
        state.discounts.getMappingFor('matterId', mid)
            .filter(d => d.entityType === 'matter')
            .map(d => d.id)
    );
    const uninvoicedDiscountIds = [
        ...invoiceDiscounts.map(d => d.id),
        ...matterDiscountIds
    ];

    // 5. FlatFees - uninvoiced for these matters
    const uninvoicedFlatFeeIds = matterFlatFees
        .filter(ff => !ff.invoiceId || ff.invoiceId === invoice.id)
        .map(ff => ff.id);

    // 6. FlatFeeMilestones - uninvoiced, belonging to matter flatFees
    const allMatterFlatFeeIds = matterFlatFees.map(ff => ff.id);
    const uninvoicedMilestoneIds = state.flatFeeMilestones
        .getMappingsFor('flatFeeId', allMatterFlatFeeIds)
        .filter(ffm => !ffm.invoiceId || ffm.invoiceId === invoice.id)
        .map(ffm => ffm.id);

    // 7. Other invoices on the matter — for "isFirstInvoice" lookups AND for the
    //    previous-balance term. A draft's `pastDue` is summed by calculatePastDue
    //    over the matter's FINALIZED siblings, so the draft's cached InvoiceInfo
    //    must invalidate when a sibling's finalized state changes — not only when
    //    a sibling is added or removed. Folding each sibling's invoiceStatus +
    //    finalizedOn into the hash makes finalizing (or un-finalizing /
    //    re-finalizing) a sibling bust the draft cache.
    //    (OWA-392: a draft stayed stuck at new-charges-only because a sibling was
    //    finalized AFTER the draft's InvoiceInfo was cached, and the prior
    //    id-only hash never changed — so the displayed total omitted the now-due
    //    previous balance.)
    const relatedInvoiceSignals = matterInvoices
        .filter(inv => inv.id !== invoice.id)
        .map(invoiceSignal); // OWA-946: dates too; they decide which invoice counts as first

    // 8. OWA-996: the labor total rounds each task to the firm's billing increment and
    //    rounding method, and finalize freezes the line items with the same two options.
    const billingSignal = `billing:${OptionHelper.getOptionValueStatic(OptionHelper.BILLING_INCREMENT)}:${OptionHelper.getOptionValueStatic(OptionHelper.ROUNDING_METHOD)}`;

    return hashIds([
        invoice.id,
        billingSignal,
        ...uninvoicedTaskIds,
        ...uninvoicedExpenseIds,
        ...uninvoicedTxIds,
        ...clientTrustSignals,
        ...matterTrustSignals,
        ...(invoice.clientIds ?? []).map(cid => `client:${cid}`),
        ...uninvoicedDiscountIds,
        ...uninvoicedFlatFeeIds,
        ...uninvoicedMilestoneIds,
        ...relatedInvoiceSignals,
    ]);
}

/**
 * Get the dependency hash for an invoice based on its finalized status.
 *
 * getInvoiceInfo recalculates for a change in the invoice or its inputs exactly when this changes.
 * Compare it, not the identity of the object getInvoiceInfo returns (OWA-1003): the cache holds one entry
 * per invoice id, so a call for a different copy of the same invoice (the saved copy while a
 * card holds an edited one) replaces the entry, and the next call returns a new object with the
 * same figures.
 */
export function getInvoiceDepsHash(invoice: Invoice): string {
    const isFinalized = invoice.finalizedOn != null && invoice.invoiceStatus === 'F';
    const prefix = isFinalized ? 'F' : 'D';
    const hash = isFinalized
        ? computeFinalizedInvoiceDepsHash(invoice)
        : computeDraftInvoiceDepsHash(invoice);
    // Include taskPositions in hash so cache invalidates when order changes
    const positionsHash = invoice.taskPositions?.join(',') ?? '';
    // Include date range so cache invalidates when invoice dates change
    const startDateStr = invoice.startDate?.getTime() ?? 'none';
    const endDateStr = invoice.endDate?.getTime() ?? 'none';
    // OWA-940: payments are bounded by issueDate, so it must bust the cache too.
    const issueDateStr = invoice.issueDate?.getTime() ?? 'none';
    // OWA-1463: the period's day boundaries are on the firm's clock, so its zone matters too.
    return `${prefix}:${invoice.addedOn}:${startDateStr}:${endDateStr}:${issueDateStr}:${firmTimeZone() ?? ''}:${hash}:${positionsHash}`;
}

/**
 * Compute dependency hash for client trust info.
 */
function computeClientTrustDepsHash(cid: uuid, invoice?: Invoice): string {
    const invoices = store.getState().invoices;
    const transactions = store.getState().transactions;

    // Get relevant invoices for this client (OWA-946: with the fields the previous-invoice
    // choice depends on, not just ids)
    const clientInvoiceIds = invoices
        .filter(inv => inv.clientIds.includes(cid))
        .map(invoiceSignal);

    // Get relevant transactions (client trust transactions)
    // Only match when clientId owns the trust account, not when they're just the payer
    const clientTxIds = transactions
        .filter(tx => (tx.sourceType === 'G' && tx.source === cid) ||
            (tx.targetType === 'G' && tx.target === cid))
        .map(txSignal);

    return hashIds([
        cid,
        // OWA-1463: day boundaries are on the firm's clock.
        `zone:${firmTimeZone() ?? ''}`,
        invoice ? invoiceSignal(invoice) : 'no-invoice',
        invoice ? `${invoice.startDate?.getTime?.() ?? ''}:${invoice.leadId ?? ''}` : '',
        ...clientInvoiceIds,
        ...clientTxIds,
    ]);
}

/**
 * Compute dependency hash for matter trust info.
 * Uses ArrayMap mappings for O(1) lookups.
 */
function computeMatterTrustDepsHash(mid: uuid, invoice: Invoice): string {
    const state = store.getState();

    // Use indexed lookups for matters
    const matterInvoiceIds = state.invoices
        .getMappingFor('matterId', mid)
        .map(invoiceSignal);

    // OWA-946: the same rule _computeMatterTrustInfo selects by (source or target is the
    // matter), taken from the trust index.
    const matterTxIds = state.transactions
        .getIndex('trustTypes')
        .filter(tx => FinancialHelper.isRelatedMatter(mid, tx))
        .map(txSignal);

    return hashIds([
        mid,
        // OWA-1463: day boundaries are on the firm's clock.
        `zone:${firmTimeZone() ?? ''}`,
        invoiceSignal(invoice),
        `${invoice.startDate?.getTime?.() ?? ''}`,
        ...matterInvoiceIds,
        ...matterTxIds,
    ]);
}

/**
 * Compute dependency hash for getTrustAmounts.
 * This is used for caching trust amounts without an invoice context.
 * Hash is based on matter ID, client IDs, and all relevant trust transactions.
 */
function computeTrustAmountsDepsHash(matter: Matter, asOf?: Date): string {
    const state = store.getState();

    // Get matter trust transactions (where matter is source or target with type 'M')
    const matterTxIds = state.transactions
        .getMappingFor('matterId', matter.id)
        .filter(tx => tx.status !== 'D' && !tx.voidedDate && !tx.refusedDate)
        .filter(tx => tx.sourceType === 'M' || tx.targetType === 'M')
        .map(tx => tx.id);

    // Get general trust transactions for all clients
    const clientTxIds: string[] = [];
    for (const clientId of matter.clients) {
        const txs = state.transactions
            .getIndex('trustTypes')
            .filter(tx => !tx.voidedDate && !tx.refusedDate)
            .filter(tx =>
                (tx.sourceType === 'G' && tx.source === clientId) ||
                (tx.targetType === 'G' && tx.target === clientId)
            );
        for (const tx of txs) {
            clientTxIds.push(tx.id);
        }
    }

    return hashIds([
        matter.id,
        ...matter.clients,
        asOf ? asOf.getTime().toString() : 'no-asof',
        // OWA-1463: an as-of day is counted on the firm's clock.
        `zone:${firmTimeZone() ?? ''}`,
        ...matterTxIds,
        ...clientTxIds,
    ]);
}

class FinancialHelper {
    static NEGATIVE_TYPES = ['DE', 'RR', 'DT', 'PT', 'PC', 'BA'];
    static NOT_TRUST_TYPES = ['BA', 'SA', 'PC', 'RO'];
    static TRUST_TYPES = ['FR', 'ST', 'TT', 'PT', 'FO', 'RR', 'DT', 'DE'];
    static STARTER_TYPES = ['ST', 'SA'];

    /**
     * Check for a 'starter' transaction: ST (starting trust amount) or SA (starting account amount).
     *
     * @param {Transaction} tr
     * @returns {boolean}
     */
    static isStarter = (tr: Transaction): boolean => FinancialHelper.STARTER_TYPES.includes(tr.txType);

    /**
     * Helper function to get an invoice by its ID.
     *
     * @param {uuid} invoiceId
     * @returns {Invoice}
     */
    static getInvoiceById(invoiceId: uuid): Invoice | null {
        return InvoiceHelper.getInvoiceFromId(invoiceId);
    }

    /**
     * Build a Set of transaction IDs that appear in any invoice's `finalValues`
     * snapshot (across payments / adjustments / withdrawals / refunds).
     *
     * The "stragglers" filter in the Transactions list deliberately uses an
     * unbounded date range and follows the client (general) trust across all
     * matters for the client — both intentional. To prevent transactions that
     * are already billed on a finalized invoice from re-surfacing on a new
     * draft, callers exclude any tx whose id is in this set.
     *
     * Pass `excludeInvoiceId` (typically the invoice currently being edited)
     * so its own finalValues entries aren't treated as "elsewhere."
     *
     * Deleted invoices are skipped: deleting an invoice releases its transactions (it clears their invoiceId but
     * keeps finalValues), so its snapshot must not keep them billed (OWA-68).
     */
    static getInvoicedTransactionIds = (excludeInvoiceId?: uuid): Set<uuid> => {
        const ids = new Set<uuid>();
        store.getState().invoices.forEach(inv => {
            if (inv.id === excludeInvoiceId || inv.status === 'D') return;
            const fv = inv.finalValues as FinalValues | null;
            if (!fv) return;
            const collect = (arr?: { transactionId?: uuid }[]) => {
                arr?.forEach(tx => {
                    if (tx?.transactionId) ids.add(tx.transactionId);
                });
            };
            collect(fv.payments);
            collect(fv.adjustments);
            collect(fv.withdrawals);
            collect(fv.refunds);
        });
        return ids;
    };

    /**
     * Is the given transaction a trust transaction?
     * @param {Transaction} tr
     */
    static isTrustTransaction(tr: Transaction): boolean {
        return FinancialHelper.TRUST_TYPES.includes(tr.txType);
    }

    /**
     * Does this transaction combination require a matter in matterIds?
     *
     * @param {Transaction} tr
     */
    static isMatterRequired(tr: Transaction): boolean {
        return (tr.txType === 'FR' && tr.targetType === 'M') ||
            (tr.txType === 'FO' && tr.targetType === 'M') ||
            (tr.txType === 'PT' && tr.sourceType === 'M') ||
            (tr.txType === 'PC') ||
            (tr.txType === 'RR' && tr.targetType === 'M') ||
            (tr.txType === 'DT' && tr.sourceType === 'M') ||
            (tr.txType === 'TT' && (tr.sourceType === 'M' || tr.targetType === 'M')) ||
            (tr.txType === 'BA');
    }

    static getRelatedMatterId(tr: Transaction): uuid | null {
        // Check for non-null matterIds
        if (tr.matterIds.length > 0) {
            const matterId = tr.matterIds[0] || tr.matterIds[1];
            if (matterId) return matterId;
        }

        // Check for related matter in source and target
        if (tr.sourceType === 'M' && tr.source) return tr.source;
        if (tr.targetType === 'M' && tr.target) return tr.target;

        else return null;
    }

    /**
     * Ensure that the transaction matches the given transaction type.
     * For example, an FR transaction should have a sourceType of 'C' and a target Type of 'M' or 'G'.
     *
     * @param {Transaction} tr
     * @returns {boolean}
     */
    static transactionFailureReason(tr: Transaction): { code: string, message: string | ReactElement } | null {
        // First check for transactions with an incomplete source, target or matterIds
        if ((tr.matterIds.length > 0) && (!tr.matterIds[0])) {
            // This is ok under certain combinations.
            if (FinancialHelper.isMatterRequired(tr)) return {code: 'TR01', message: 'A lead or case is required, but none found'};
        }
        else if ((tr.clientIds.length > 0) && (!tr.clientIds[0])) return {code: 'TR02', message: 'Clients not found'};
        else if (tr.sourceType && !tr.source) return {code: 'TR03', message: 'No source found'};
        else if (tr.targetType && tr.targetType !== 'B' && !tr.target) {
            // Check if this is a distribution transaction
            if (tr.paymentDistribution?.length > 0) {
                // Check the distribution targets
                if (!tr.paymentDistribution.every((dist) => find<Matter>('matters', dist.matterId)))
                    return {code: 'TR04', message: 'Distribution target(s) not found'};
                else return null;
            }
            return {code: 'TR05', message: 'No target found'};
        }
        else {
            // Check to see if there is an associated matter or lead, and that the entityType is correct for it.
            const matter = tr.matterIds.length > 0 && tr.matterIds[0] ? find<Matter>('matters', tr.matterIds[0]) : null;

            if (tr.entityType === 'matter') {
                // Check for a leadId with an entityType of 'matter'
                if (!matter) {
                    const lead = find<Lead>('leads', tr.matterIds[0]);
                    if (lead) return {code: 'TR06', message: <div className='u-sentence'>Case <CopyText useCodeStyle={true} text={tr.matterIds[0]}/> is associated with a lead, not a case</div>};
                    else {
                        let contact = find<Contact>('contacts', tr.matterIds[0]);
                        if (contact) return {
                            code: 'TR07',
                            message: <div className='u-sentence'>Case <CopyText useCodeStyle={true} text={tr.matterIds[0]}/> is associated with a contact, not a case</div>
                        };
                        else return {code: 'TR08', message: <div className='u-sentence'>Case <CopyText useCodeStyle={true} text={tr.matterIds[0]}/> is not found (possibly deleted)</div>};
                    }
                }
            }
            else if (tr.entityType === 'lead') {
                const lead = find<Lead>('leads', tr.matterIds[0]);

                // Check for a leadId with an entityType of 'matter'
                if (!lead) {
                    if (matter) return {code: 'TR09', message: <div className='u-sentence'>Lead <CopyText useCodeStyle={true} text={tr.matterIds[0]}/> is associated with a case, not a lead</div>};
                    else {
                        let contact = find<Contact>('contacts', tr.matterIds[0]);
                        if (contact) return {
                            code: 'TR10',
                            message: <div className='u-sentence'>Lead <CopyText useCodeStyle={true} text={tr.matterIds[0]}/> is associated with a contact, not a lead</div>
                        };
                        else return {code: 'TR11', message: <div className='u-sentence'>Lead <CopyText useCodeStyle={true} text={tr.matterIds[0]}/> is not found (possibly deleted)</div>};
                    }
                }
            }
        }

        // Then check for bad assignments
        let isOk: boolean | null = null;
        let code = '';

        switch (tr.txType) {
            case 'FR': // Fund Retainer
                isOk = tr.sourceType === 'C' && (tr.targetType === 'G' || tr.targetType === 'M');
                code = 'TR12';
                break;
            case 'PT': // Payment from Trust
                isOk = (tr.sourceType === 'C' || tr.sourceType === 'G' || tr.sourceType === 'M') && tr.targetType === 'B';
                code = 'TR13';
                break;
            case 'PC': // Payment from Client
                isOk = tr.sourceType === 'C' && tr.targetType === 'B';
                code = 'TR14';
                break;
            case 'TT':
                isOk = (tr.sourceType === 'G' || tr.sourceType === 'M' || tr.sourceType === 'B') && (tr.targetType === 'G' || tr.targetType === 'M');
                code = 'TR15';
                break;
            case 'BA': // Balance Adjustment
                isOk = tr.sourceType === null && tr.targetType === 'M';
                code = 'TR16';
                break;
            case 'DT':
                isOk = (tr.sourceType === 'G' || tr.sourceType === 'M') && tr.targetType === 'C';
                code = 'TR17';
                break;
            case 'DE': // Disburse Escrow
                isOk = tr.sourceType === 'E' && tr.targetType === 'C';
                code = 'TR18';
                break;
            case 'FO': // Fund Operating
                isOk = tr.sourceType === 'B' && (tr.targetType === 'G' || tr.targetType === 'M');
                code = 'TR19';
                break;
            case 'ED': // Escrow Deposit
                isOk = tr.sourceType === 'C' && tr.targetType === 'E';
                code = 'TR20';
                break;
            case 'RO': // Refund from Operating
                isOk = tr.sourceType === 'B' && tr.targetType === 'C';
                code = 'TR21';
                break;
            case 'RR': // Refund Retainer from Trust
                isOk = (tr.sourceType === 'G' || tr.sourceType === 'M') && tr.targetType === 'C';
                code = 'TR22';
                break;
            case 'ST': // Starting Trust
                isOk = tr.sourceType === null && (tr.targetType === 'G' || tr.targetType === 'M');
                code = 'TR23';
                break;
            case 'SA': // Starting Account
                isOk = tr.sourceType === null && tr.targetType === 'M';
                code = 'TR24';
                break;
        }

        if (!isOk) return {code, message: <div className='u-sentence'>Source and target types do not match transaction type</div>};

        /** Invoicing Checks */
        if (tr.invoiceId && (tr.voidedDate || tr.refusedDate)) {
            const invoice = store.getState().invoices.get(tr.invoiceId);
            if (!invoice) return {code: 'TR30', message: <div className='u-sentence'>Associated invoice <CopyText useCodeStyle={true} text={tr.invoiceId}/> is not found (possibly deleted)</div>};
            const vOrF = tr.voidedDate ? 'voided' : 'refused';
            const issueDate = format(invoice.issueDate, 'yyyy-MM-dd');
            const msg = `${issueDate} ${invoice.invoiceNumber}`;
            return {code: 'TR34', message: <div className='u-sentence'>This transaction is marked {vOrF}, but is included on invoice <CopyText useCodeStyle={true} text={msg}/></div>};
        }
        else if (tr.invoiceId) {
            const invoice = store.getState().invoices.get(tr.invoiceId);

            // Check for transactions that have an invoiceId, but the invoice is deleted or not found
            if (!invoice) return {code: 'TR30', message: <div className='u-sentence'>Associated invoice <CopyText useCodeStyle={true} text={tr.invoiceId}/> is not found (possibly deleted)</div>};

            // Check for transactions that have an invoiceId, but are not in the transactionIds of the related invoice
            else if (!invoice.transactionIds.includes(tr.id)) {
                return {
                    code: 'TR31',
                    message: <div className='u-sentence'>Transaction is marked with invoice <CopyText useCodeStyle={true} text={invoice.invoiceNumber}/>, but the transaction is not included in the
                        invoice</div>
                };
            }

        }
        else if (!tr.voidedDate && !tr.refusedDate && !['SA', 'ST'].includes(tr.txType)) {
            // Check for transactions that probably should be part of an invoice, but have no invoiceId
            const invoice = store.getState().invoices.find(invoice => invoice.invoiceStatus === InvoiceStatus.Final && invoice.transactionIds.includes(tr.id));
            if (invoice) return {
                code: 'TR32',
                message: <div className='u-sentence'>Invoice <CopyText useCodeStyle={true} text={invoice.id}/> [{invoice.invoiceNumber}] includes this transaction, but the transaction is not
                    associated with the invoice</div>
            };
            else {
                // Check for transactions that are inside an invoice range (but not related)
                const dateRanges = FinancialHelper.getCandidateInvoicesForTransaction(tr);
                const candidates = dateRanges.map(range => {
                    const issueDate = format(range.invoice.issueDate, 'yyyy-MM-dd');
                    const invoiceNumber = range.invoice.invoiceNumber;
                    return <CopyText key={invoiceNumber} useCodeStyle={true} text={`${issueDate} ${invoiceNumber}`}/>;
                })
                if (candidates.length > 0) return {
                    code: 'TR33',
                    message: <div className='u-sentence'>Transaction is not associated with an invoice, but it could be part of one of the following invoices: {candidates}</div>
                };
            }
        }

        // Didn't find any problems, return null
        return null;
    }

    /**
     * Convert an id to a contact, matter or bank account
     * @param {uuid} id
     * @returns {string}
     */
    static getSourceTarget = (id: uuid): string => {
        const contact = store.getState().contacts.get(id);
        if (contact) return formatFML(contact);
        else {
            const matter = store.getState().matters.get(id);
            if (matter) return matter.name;
            else {
                const ba = store.getState().bankAccounts.get(id);
                if (ba) return ba.name;
                else return id;
            }
        }
    }

    static decodeTransactionType(transactionType: string): string {
        switch (transactionType) {
            case 'FR':
                return 'Fund Retainer';
            case 'PT':
                return 'Payment from Trust';
            case 'PC':
                return 'Payment from Client';
            case 'TT':
                return 'Trust Transfer';
            case 'BA':
                return 'Balance Adjustment';
            case 'DT':
                return 'Deposit to Client';
            case 'DE':
                return 'Disburse Escrow';
            case 'FO':
                return 'Fund Operating';
            case 'ED':
                return 'Escrow Deposit';
            case 'RO':
                return 'Refund from Operating';
            case 'RR':
                return 'Refund Retainer from Trust';
            case 'ST':
                return 'Starting Trust';
            case 'SA':
                return 'Starting Account';
            default:
                return 'Unknown';
        }
    }

    static decodeTransactionCode(code: string): string {
        switch (code) {
            case 'C':
                return 'Client';
            case 'G':
                return 'General Trust';
            case 'M':
                return 'Case Trust';
            case 'B':
                return 'Operating';
            case 'E':
                return 'Escrow';
            default:
                return 'Unknown';
        }
    }

    /**
     * Return true if this invoiceId is related to a finalized invoice, false otherwise.
     *
     * @param {uuid} invoiceId
     * @returns {boolean}
     */
    static getFinalizedByInvoiceId(invoiceId: uuid): boolean {
        // Always false if no invoiceId passed!
        if (!invoiceId) return false;

        // Rehydrate the invoice
        const invoice = this.getInvoiceById(invoiceId);

        // If not found, leave a logged message (not an error at this point)
        // This can happen when an invoice is deleted, but the invoiceId is still in the task.
        if (!invoice) {
            if (store.getState().misc.restoreStatus === RestoreStatus.Restored)
                console.log('Could not find invoice for id', invoiceId);

            return false;
        }
        // Otherwise return the answer
        else return this.getFinalizedByInvoice(invoice);
    }

    /**
     * Double-check that an invoice is finalized: look for the invoiceStatus of 'F' and a finalized date.
     *
     * @param {Invoice} invoice
     * @returns {boolean}
     */
    static getFinalizedByInvoice = isFinalizedInvoice;

    /**
     * True when a finalized invoice lists this task or the task points at one (OWA-1644):
     * its timer cannot be started or edited.
     */
    static isTaskTimerLocked(task: {id: string; invoiceId?: uuid | null}): boolean {
        return isTaskTimerLockedByFinalizedInvoice(task, store.getState().invoices);
    }

    /**
     * Helper function to sort a list of transactions by date.
     * We always put starter transactions FIRST.
     *
     * @param {Transaction} a
     * @param {Transaction} b
     * @returns {number}
     */
    static sortTransactionsByDate = (a: Transaction, b: Transaction): number => FinancialHelper.isStarter(a)
                                                                                ? -1
                                                                                : FinancialHelper.isStarter(b)
                                                                                  ? 1
                                                                                  : FinancialHelper.sortTransactionsByType(a, b);

    /**
     * Get the one and only operating account.
     *
     * @returns {BankAccount}
     */
    static getOperatingAccount(): BankAccount | null {
        return store.getState().bankAccounts.find(ba => ba.accountType === 'O');
    }

    /**
     * Should we attach/require an invoiceId to this transaction?
     *
     * @param {Transaction} tx
     * @returns {boolean}
     */
    static useInvoiceId = (tx: Transaction): boolean => {
        // If there is a client trust involved, we never attach an invoiceId because the client trust can be used for
        // multiple accounts.
        if ([tx.sourceType, tx.targetType].includes('G')) return false;

        // If this is a starter transaction, we attach the invoiceId
        else if (FinancialHelper.isStarter(tx)) return true;

        // If it is an escrow transaction, then we always use an invoiceId.
        else if (isEscrow(tx.txType)) return true;

        // Balance adjustments are only tied if attached to a matterId
        else if (isBalanceAdjustment(tx.txType)) return tx.targetType === 'M';

        // All other cases we use the invoiceId
        else return false;
    };

    /**
     * Create a transaction where funds are deposited into a general or matter trust by the client or a responsible payer.
     *
     * @param {uuid} contactId
     * @param {'G'|'M'} targetType
     * @param {uuid} contactOrMatterId
     */
    static createFundRetainerFromContact(contactId: uuid, targetType: 'G' | 'M', contactOrMatterId: uuid) {
        return _generateTransactionSkeleton({
            txType: 'FR',
            sourceType: 'C',
            source: contactId,
            targetType,
            target: contactOrMatterId
        });
    }

    /**
     * Create a transaction where funds are moved from operating account to a general or matter trust.
     * This only happens as a side effect of a payment where most of the payment goes toward earned fees,
     * and the rest goes into trust.
     *
     * @param {uuid} bankAccountId - an operating account
     * @param {'G'|'M'} targetType
     * @param {uuid} contactOrMatterId
     * @returns {Partial<Transaction>}
     */
    static createFundRetainerFromOperating(bankAccountId: uuid, targetType: 'G' | 'M', contactOrMatterId: uuid): Partial<Transaction> {
        return _generateTransactionSkeleton({
            txType: 'FO',
            sourceType: 'B',
            source: bankAccountId,
            targetType,
            target: contactOrMatterId
        });
    }

    /**
     * Create a transaction where money from a contact (client or 3rd party) is deposited into an escrow account.
     *
     * @param {uuid} contactId
     * @param {uuid} escrowId
     * @returns {Partial<Transaction>}
     */
    static createEscrowDeposit(contactId: uuid, escrowId: uuid): Partial<Transaction> {
        return _generateTransactionSkeleton({
            txType: 'ED',
            sourceType: 'C',
            source: contactId,
            targetType: 'E',
            target: escrowId
        });
    }

    /**
     * Create a transaction where money from a trust is used to pay an invoice.
     *
     * @param {'G'|'M'} sourceType
     * @param {uuid} contactOrMatterId - money is coming from a general/matter trust
     * @param {uuid} bankAccountId
     * @returns {Partial<Transaction>}
     */
    static createPaymentFromTrust(sourceType: 'G' | 'M', contactOrMatterId: uuid, bankAccountId: uuid): Partial<Transaction> {
        return _generateTransactionSkeleton({
            txType: 'PT',
            sourceType,
            source: contactOrMatterId,
            targetType: 'B',
            target: bankAccountId
        });
    }

    /**
     * Create a transaction where money from a trust is used to pay an invoice.
     *
     * @param {uuid} contactId - money is coming from a contact
     * @param {uuid} bankAccountId
     * @returns {Partial<Transaction>}
     */
    static createPaymentFromContact(contactId: uuid, bankAccountId: uuid): Partial<Transaction> {
        return _generateTransactionSkeleton({
            txType: 'PC',
            sourceType: 'C',
            source: contactId,
            targetType: 'B',
            target: bankAccountId
        });
    }

    /**
     * Create a transaction where money from a trust is being returned to the client, generally at the end of a case.
     *
     * @param {'G'|'M'} sourceType
     * @param {uuid} contactOrMatterId - money is coming from a general/matter trust
     * @param {uuid} contactId - ID of the client
     * @returns {Partial<Transaction>}
     */
    static createRefundRetainer(sourceType: 'G' | 'M', contactOrMatterId: uuid, contactId: uuid): Partial<Transaction> {
        return _generateTransactionSkeleton({
            txType: 'RR',
            sourceType,
            source: contactOrMatterId,
            targetType: 'C',
            target: contactId
        });
    }

    /**
     * Create a transaction where money from a trust is used to pay a third party.
     *
     * @param {'G'|'M'|'E'} sourceType
     * @param {uuid} contactMatterOrEscrowId - money is coming from a general/matter/escrow trust
     * @param {uuid} contactId
     * @returns {Partial<Transaction>}
     */
    static createDisbursement(sourceType: 'G' | 'M' | 'E', contactMatterOrEscrowId: uuid, contactId: uuid): Partial<Transaction> {
        return _generateTransactionSkeleton({
            txType: 'DT',
            sourceType,
            source: contactMatterOrEscrowId,
            targetType: 'C',
            target: contactId
        });
    }

    /**
     * Create a transaction where money from a trust is moved to another trust
     *
     * @param {'G'|'M'|'E'} sourceType
     * @param {uuid} source - money is coming from a general/matter/escrow trust
     * @param {'G'|'M'|'E'} targetType
     * @param {uuid} target - money is going to a general/matter/escrow trust
     * @returns {Partial<Transaction>}
     */
    static createTrustTransfer(sourceType: 'G' | 'M' | 'E', source: uuid, targetType: 'G' | 'M' | 'E', target: uuid): Partial<Transaction> {
        return _generateTransactionSkeleton({
            txType: 'TT',
            sourceType,
            source,
            targetType,
            target
        });
    }

    /**
     * Create a transaction showing a credit or debit to the specified account
     *
     * @param {'G'|'M'|'E'} sourceType
     * @param {uuid} contactMatterOrEscrowId
     * @returns {Partial<Transaction>}
     */
    static createBalanceAdjustment(sourceType: 'G' | 'M' | 'E', contactMatterOrEscrowId: uuid): Partial<Transaction> {
        return _generateTransactionSkeleton({
            txType: 'BA',
            sourceType,
            source: contactMatterOrEscrowId
        });
    }

    /**
     *
     * @param {uuid} matterId
     * @param {Transaction} tx
     * @returns {boolean}
     */
    static isRelatedMatter = (matterId: uuid, tx: Transaction): boolean => {
        return tx.source === matterId ||
            tx.target === matterId;
    }

    /**
     * Get the amount of a payment, specific to the matterId if part of a distribution
     * @param {Transaction} tx
     * @param {uuid} matterId
     * @returns {number}
     */
    static getPaymentAmount = (tx: Transaction, matterId: uuid | null | undefined): number => {
        if (tx.paymentDistribution.length > 0) {
            const pd = tx.paymentDistribution.find(pd => pd.matterId === matterId);
            if (pd) return Number.parseFloat(pd.amount);
            else return 0.0;
        }
        else return tx.amount;
    }

    /**
     * Get the amount of this transaction applied to a matter or client.
     * Called from withing Contact or Matter, we want to see what portion of the transaction affected the trust balance.
     * Either client or matter may be set, but NOT both.
     *
     * @param {Transaction} tx
     * @param {'A'|'G'|'M'} trustType
     * @param {uuid} targetId
     * @returns {number}
     */
    static getTransactionAmount(tx: Transaction, trustType: 'A' | 'G' | 'M', targetId: uuid): number {
        // If this transaction was voided or refused, ignore it
        if (!(tx.refusedDate || tx.voidedDate || tx.status === 'D')) {

            // For payments, we add the transaction amount to the total if a portion of the distribution
            // applies to this matter
            if (isPayment(tx.txType)) {

                // If the target matches and the targetType is A or matches trustType
                if ((trustType === 'A' || tx.targetType === trustType) && tx.target === targetId) {
                    return tx.amount;
                }

                // If this payment is split, check to see if any portion applies
                else if (tx.paymentDistribution.length > 0 && ['M', 'A'].includes(trustType)) {
                    if (tx.paymentDistribution.find(pd => pd.matterId === targetId)) {
                        return Number.parseFloat(tx.paymentDistribution.find(pd => pd.matterId === targetId)!.amount);
                    }
                }
            }

            // For withdrawals, check for a matching trustType/id and delete from total
            else if (isWithdrawal(tx.txType)) {
                if (tx.sourceType === trustType && tx.source === targetId) {
                    return -1.0 * tx.amount;
                }
            }

                // For a transfer, if this is NOT a TT, add to the trust if the target matches,
            // remove if the source matches
            else if (isTransfer(tx.txType)) {
                if (tx.sourceType === trustType && tx.source === targetId) {
                    return -1.0 * tx.amount;
                }
                else if (tx.targetType === trustType && tx.target === targetId) {
                    return tx.amount;
                }
            }
        }
        else return 0;

        // Transaction doesn't apply to this trust/target.
        return 0;
    };

    /**
     * Get trust accounts for the clients and matters.
     * There will be one trust per matter, and one trust for each client in common to all matters.
     *
     * However, if this is a payment, and there are multiple matters selected, there are three options:
     * 1. Put each distribution into the related client trust
     * 2. Put the whole amount into the client trust.
     * 3. Put the whole amount into
     *
     * Payments: trusts are used as a target (payment is always an external entity).
     *
     * Need to look at the 'field' parameter (to/from) to make sure we are not allowing the same to/from funding source.
     *
     * @param {uuid[]} clientIds
     * @param {Matter} matter - related matter, if any
     * @param {string[]} types
     * @return {TrustAccountSelectOption}
     */
    static getTrustAccounts = (clientIds: uuid[], matter: Matter, types: string[]): TrustAccountSelectOption[] => {
        // Get the related matters
        const matters = CaseHelper.getMattersForClients(clientIds);

        // If not clients in common, we can not pull from any client trusts.
        const clientTrusts = types.includes('G')
                             ? clientIds
                                 .map(cid => {
                                     // Get the balance for this trust
                                     const contact = find<Contact>('contacts', cid);
                                     const clientName = formatLFM(contact, true);
                                     const trustBalance = FinancialHelper.getTrustAmount('G', cid);

                                     return {
                                         id: cid,
                                         type: 'G',
                                         name: FinancialHelper.formatLabel(Icons.USER, i18n.cvs('trustFor', {value: clientName}), trustBalance, 'blue'),
                                         balance: trustBalance
                                     };
                                 })
                             : [];

        // Add the trust for the matter
        const matterTrusts = types.includes('M') ? matters
            .map(matter => {
                const trustAmount = FinancialHelper.getTrustAmount('M', matter.id);

                return {
                    id: matter.id,
                    type: 'M',
                    name: FinancialHelper.formatLabel(Icons.CASE, i18n.cvs('trustFor', {value: matter.name}), trustAmount, 'brown'),
                    balance: trustAmount
                };
            }) : [];

        const merged = matter ? [...matterTrusts, ...clientTrusts] : [...clientTrusts, ...matterTrusts];

        return merged.map(account => {
            return {
                label: account.name,
                value: account.id,
                data: account
            };
        });
    };

    /**
     * Get the portion of a transaction pertaining to a matter. This is normally just the amount,
     * unless it is a split transaction.
     *
     * @param {uuid} matterId
     * @param {Transaction} tr
     * @returns {number}
     */
    static getTransactionPortionForMatter = (matterId: uuid, tr: Transaction): number => {
        if (tr.paymentDistribution?.length > 0) {
            const pd = tr.paymentDistribution.find(pd => pd.matterId === matterId);
            if (pd) return Number.parseFloat(pd.amount);
            else return 0.0;
        }
        else return tr.amount;
    }

    /**
     * Get the current amount due, as of the latest invoice.
     *
     * @param {Matter} matter
     * @returns {number}
     */
    static getAmountDue = (matter: Matter): number => {
        const lifetime = FinancialHelper.getLifeTimeStats(matter);

        if (lifetime) {
            const {totalBilled, totalPaid} = lifetime;
            return totalBilled - totalPaid;
        }
        else return 0;
    };

    /**
     * Get information needed to determine red rubber band status
     * @param {Matter} matter
     * @returns {RedRubberBandInfo}
     */
    static getRedRubberBandInfo = (matter: Matter): RedRubberBandInfo => {
        const unbilled = util.toTwoDecimals(CaseHelper.getTotalUnbilled(matter));
        const due = util.toTwoDecimals(FinancialHelper.getAmountDue(matter));

        const {totalTrust} = FinancialHelper.getTrustAmounts(matter);
        const trust = util.toTwoDecimals(totalTrust);

        // If this is a flat fee matter, we don't red rubber band, but we do mark it as restricted.
        const isRestricted = (matter.maxClientFunds ?? 0) > 0 || matter.rateType.trim() === 'f';
        const isRedRubberBanded = !isRestricted &&
            ((matter.minimumBalance ?? 0) > 0) &&
            (unbilled + due > trust);
        const isWarning = !isRedRubberBanded &&
            (trust > 0) &&
            ((unbilled + due) / trust > .8);

        const rrb = isRedRubberBanded ? 'u-red-rubber-band' : '';
        const isr = isRestricted ? 'u-restricted' : '';
        const isw = isWarning ? 'u-threshold' : '';
        const warningColor = isw || isr || rrb;

        return {unbilled, due, trust, isRestricted, isRedRubberBanded, isWarning, warningColor};
    }

    /**
     * Get the total amount billed and spent for a case (to date).
     *
     * @param {Matter} matter
     * @returns Net billed, cash paid and invoice discounts plus current billing adjustments.
     */
    static getLifeTimeStats = (matter: Matter): { totalBilled: number; totalPaid: number; billingReductions: number; } => {
        // Existing revenue consumers display net billed and cash paid. Noncash
        // credits lower billed; they settle receivables without becoming revenue.
        const allocation = FinancialHelper.getMoneyAllocation([matter.id], undefined, [matter]);
        const credits = moneyCheckedSum(new Float64Array(allocation.credits.map(credit => credit.amount)));
        const discounts = moneyCheckedSum(new Float64Array(allocation.invoices.map(row => row.discount)));

        return {
            totalBilled: moneyCheckedSubtract(allocation.totalBilled, credits),
            totalPaid: moneyCheckedSubtract(allocation.totalSettled, credits),
            // Discounts are already in invoice principal. Expose them here without
            // subtracting them from net billed again. Credit reversals remain signed.
            billingReductions: moneyCheckedAdd(discounts, credits),
        };
    }

    /**
     * OWA-551: the CURRENT outstanding balance for every FINALIZED invoice across
     * all matters, WITHOUT ever mutating the immutable finalized invoices.
     *
     * A finalized invoice is the legal document sent to the client; its frozen
     * `finalValues` never change. But what the client owes *right now* must also
     * reflect activity since finalize. This delegates to the single allocator,
     * {@link #getBilledAndPaidForMatters}, which applies every balance-affecting
     * transaction (payments, adjustments, refunds — including post-finalize ones)
     * oldest-invoice-first. Exposing the result as invoiceId → current balance
     * keeps the outstanding-balance reports (AR Aging, dashboard Outstanding /
     * Overdue KPIs) consistent with the invoice-list status and the InvoiceCard
     * paid icon, which use the same allocator.
     * See client/src/Financial/Invoices/INVOICE_ASSOCIATION_INVARIANT.md.
     *
     * @returns {Map<uuid, number>} finalized invoiceId → current outstanding (≥ 0)
     */
    static getAllFinalizedOutstandingBalances = (): Map<uuid, number> => {
        const result = new Map<uuid, number>();
        // Cache the allocator result per billing-entity key so invoices that share
        // a matter (or matter-set / lead) are computed once, not per invoice.
        const bapCache = new Map<string, BilledAndPaidInvoice[]>();
        store.getState().invoices
            .filter(inv => inv.invoiceStatus === InvoiceStatus.Final && inv.status !== 'D')
            .forEach((inv: Invoice) => {
                // Use the SAME billing-entity ids the invoice-list status and the
                // InvoiceCard paid icon use (leadId, else full matterIds), so the
                // reports stay consistent with them for single-matter, multi-matter
                // AND lead invoices — then read this invoice's own row.
                const ids = inv.leadId ? [inv.leadId] : (inv.matterIds ?? []);
                if (ids.length === 0) return;
                const key = inv.leadId ?? [...ids].sort().join(',');
                let baps = bapCache.get(key);
                if (!baps) {
                    baps = FinancialHelper.getBilledAndPaidForMatters(ids);
                    bapCache.set(key, baps);
                }
                const bap = baps.find(b => b.invoiceId === inv.id);
                if (bap) result.set(inv.id, Math.max(0, bap.billed - bap.collected));
            });
        return result;
    };

    /**
     * Is this case red-rubber-banded?
     * @param {Matter} matter
     * @returns {boolean}
     */
    static isRedRubberBanded = (matter: Matter): boolean => {
        const {unbilled, due, trust} = FinancialHelper.getRedRubberBandInfo(matter);

        return (unbilled + due) > trust;
    }

    /**
     * Find all transactions for the given trust type and (optional) id, then add them up based on how they affect
     * the balance (positive or negative).
     *
     * @param {SourceTypeType} trustType - 'G' for client trust, 'M' for matter trust, 'E' for Escrow, and 'A' for all (non-Escrow) trusts
     * @param {uuid} [targetId] - this is either a contactId or a matterId (for leads, it is the contactId), or null (all trusts)
     * @param {object} [options]
     * @param {Date} [options.asOf] - we only look at transactions ON OR AFTER this date. A calendar day (its local
     *   year, month and day), compared with each transaction's day on the firm's clock (OWA-1463); to pass an instant
     *   such as another transaction's txDate, convert it with firmCalendarDay first

     * @param {boolean} [options.excludeAsOf] - do not include the asOf date in matching (strictly before or after)
     * @param {boolean} [options.reverseAsOf] - flip asOf to be only transactions ON OR BEFORE this date
     * @param {boolean} [options.useExactDate] - do not mess with dates
     * @param {boolean} [options.invoicedOnly] - only use invoiced transactions
     * @param {uuid} [options.invoiceId] - only use transactions for this invoiceId
     * @return {number}
     */
    static getTrustAmount = (trustType: SourceTypeType | null | undefined, targetId?: uuid | null, options?: {
        asOf?: Date;
        excludeAsOf?: boolean;
        reverseAsOf?: boolean;
        useExactDate?: boolean;
        invoicedOnly?: boolean;
        invoiceId?: uuid;
        excludeTxId?: uuid;
    }): number => {
        // Put all amounts into a total that we will return
        let total = 0;
        const asOf = options?.asOf;
        const excludeAsOf = options?.excludeAsOf;
        const reverseAsOf = options?.reverseAsOf;
        const useExactDate = options?.useExactDate;
        const invoicedOnly = options?.invoicedOnly;
        const invoiceId = options?.invoiceId;
        const excludeTxId = options?.excludeTxId;
        const zone = firmTimeZone();

        // Look through all transactions for the given client or matter trust
        store.getState().transactions
            .getIndex('trustTypes')
            .filter(tx => {
                // Throw out deleted, voided or refused transactions
                if (tx.status === 'D') return false;

                // The caller is replacing this row, so it must not count against itself
                if (excludeTxId && tx.id === excludeTxId) return false;

                // voided or refused will not move the needle.
                if (tx.voidedDate || tx.refusedDate) return false;

                // If only invoiced
                if (invoicedOnly && !tx.invoiceId) return false;

                // Quick path for invoiced
                if (trustType === 'M' && !!invoiceId && (tx.invoiceId === invoiceId)) return true;

                // If specific invoiceId
                if (invoiceId && tx.invoiceId !== invoiceId) return false;

                // If looking for a specific type of trust, bail if wrong type
                if (trustType !== 'A' && ![tx.sourceType, tx.targetType].includes(trustType)) return false;

                // If a targetId is given, make sure the transaction includes it as a source or target
                if (targetId) {
                    // For general trust, only match when the clientId owns the trust account
                    // (not just any party to the transaction - e.g., the payer)
                    if (trustType === 'G') {
                        const isTargetMatch = tx.targetType === 'G' && tx.target === targetId;
                        const isSourceMatch = tx.sourceType === 'G' && tx.source === targetId;
                        if (!isTargetMatch && !isSourceMatch) return false;
                    }
                    // For matter trust, also check paymentDistribution for split funding
                    else {
                        const hasSplitDistribution = tx.txType === 'FR' &&
                            tx.paymentDistribution?.some(pd => pd.matterId === targetId);
                        if (!hasSplitDistribution && !FinancialHelper.isRelatedMatter(targetId, tx)) {
                            return false;
                        }
                    }
                }

                // Ignore starting amounts, as they have nothing to do with trusts
                if (tx.txType === 'SA') return false;

                // Ignore FR if source or target is not the target
                if (tx.txType === 'FR' &&
                    !(tx.source === targetId ||
                        tx.target === targetId ||
                        tx.paymentDistribution?.some(pd => targetId === pd.matterId))
                ) return false;

                // Check the dates if we are doing an asOf. OWA-1463: asOf is a calendar day, and a
                // transaction counts on its day on the firm's clock, not the viewer's.
                if (asOf) {
                    const txDate = useExactDate
                                   ? tx.txDate
                                   : firmCalendarDay(tx.txDate, zone);
                    const asOfDate = useExactDate
                                     ? asOf
                                     : DateUtil.startOfDay(asOf);

                    if (reverseAsOf) {
                        // We want to exclude transactions that come before the asOf date
                        if (excludeAsOf && (txDate <= asOfDate)) return false;
                        else if (txDate < asOfDate) return false;
                    }
                    else {
                        // We want to exclude transactions that come after the asOf date
                        if (excludeAsOf && (txDate >= asOfDate)) return false; // exclude the date or better
                        else if (txDate > asOfDate) return false; // exclude only
                    }
                }

                // If we are looking for all non-escrow transactions, we need a source or target with a 'G' or an 'M'
                return !((trustType === 'A') &&
                    (util.arrayIntersection([tx.sourceType, tx.targetType], ['G', 'M']).length === 0));
            })
            .sort(FinancialHelper.sortTransactionsByDate) // start by sorting all the transactions (ST goes first), in date order ASC
            .forEach((tx, index) => {
                const isRelated = targetId ? FinancialHelper.isRelatedMatter(targetId, tx) : true;
                const targetMatches = targetId ? tx.target === targetId : true;
                const sourceMatches = targetId ? tx.source === targetId : true;

                // For payments, we add the transaction amount to the total if a portion of the distribution
                // applies to this matter
                if (isPayment(tx.txType) || isBalanceAdjustment(tx.txType)) {
                    // MUST have a related matter
                    // if (!isRelated) util.noop();

                    // Trust payments where the matches subtracts from total
                    if (tx.txType === 'PT' && sourceMatches) total -= tx.amount;

                        // If this payment is split, check to see if any portion applies
                    // We can only split a trust payment between multiple case trusts, not between a case and client trust!
                    else if (tx.txType === 'FR' && tx.paymentDistribution?.length > 0) {
                        const amt = Number.parseFloat(tx.paymentDistribution.find(t => t.matterId === targetId)?.amount ?? '') || 0;
                        total += FinancialHelper.isNegative(tx, targetId ? [targetId] : [])
                                 ? -1.0 * amt
                                 : amt;
                    }

                    // Balance adjustments and "start trust" types always add to the balance, because they can have negative values
                    else if ((tx.txType === 'BA' || tx.txType === 'ST') && targetMatches) {
                        total += (FinancialHelper.isNegative(tx, targetId ? [targetId] : []) ? -1 : 1) * tx.amount;
                    }

                    // Otherwise, if this transaction's target matches, we add
                    else if ((trustType === 'A' || tx.targetType === trustType) && isRelated/*targetMatches */) {
                        // Note that Balance Adjustments can be negative, so we don't have to check for that here.
                        if (tx.paymentDistribution?.length > 0) total += FinancialHelper.getPaymentAmount(tx, targetId);
                        else total += tx.amount;
                    }

                    // Oops
                    else console.warn('Fell out:', tx.txType, tx.amount, sourceMatches);
                }

                // For withdrawals, check for a matching trustType/id and subtract from total
                else if (isWithdrawal(tx.txType)) {
                    if (tx.sourceType === trustType && sourceMatches) {
                        total -= tx.amount;
                    }
                }

                    // For a transfer, if this is NOT a TT, add to the trust if the target matches,
                // remove if the source matches
                else if (isTransfer(tx.txType)) {
                    if (tx.sourceType === trustType && sourceMatches) {
                        total -= tx.amount;
                    }
                    else if (tx.targetType === trustType && targetMatches) {
                        total += tx.amount;
                    }
                }

                else console.warn(trustType, targetId, index, 'Dropped out of loop', tx.txType, tx.amount);
            });

        // Scale-4 result; callers round at display boundary — OWA-451.
        return total;
    }

    /**
     * Why a trust movement (RR, DT, PT, TT) may not be saved yet, or null when it may (OWA-1524, OWA-1536, OWA-1535).
     *
     * One rule for every card: the amount is positive, and a trust (client or Case) is never overdrawn.
     * A negative amount would reverse the money's direction, so it is refused before any balance is read.
     * A source that is not a trust (the operating account, 'B') is not checked: Outlaw does not track the
     * operating bank balance, so any figure here would be a guess (TRX-070, TRX-073).
     *
     * The balance is the trust kernel's balance as of the movement's date, without the stored copy of this
     * same row, so editing an existing row never counts its own amount twice.
     *
     * @param {Transaction} tr
     * @returns {'amount'|'funds'|null}
     */
    static getTrustMovementBlock = (tr: Transaction): 'amount' | 'funds' | null => {
        if (!Number.isFinite(tr.amount) || tr.amount <= 0) return 'amount';

        // Nothing to overdraw without a trust source (a missing source is the card's own need-a-source message)
        if (!tr.source || (tr.sourceType !== 'G' && tr.sourceType !== 'M')) return null;

        // A voided or refused row moves no money
        if (tr.voidedDate || tr.refusedDate) return null;

        const balance = FinancialHelper.getTrustAmount(tr.sourceType, tr.source, {
            asOf: tr.txDate ? firmCalendarDay(tr.txDate) : undefined,
            excludeTxId: tr.id,
        });

        return balance >= tr.amount ? null : 'funds';
    }

    /**
     * Point a payment from trust (PT) at the Case whose trust pays it (OWA-1530).
     * The Case is the SOURCE. The target stays the operating account (shape TR13), so the row shows as an
     * operating receipt and never as money into the Case trust.
     *
     * @param {Transaction} tr - mutated
     * @param {uuid} matterId
     */
    static setPaymentFromTrustCase = (tr: Transaction, matterId: uuid): void => {
        tr.matterIds = [matterId];
        tr.sourceType = 'M';
        tr.source = matterId;
        tr.paymentMethod = 'trust';

        if (tr.targetType !== 'B' || !tr.target) {
            tr.targetType = 'B';
            tr.target = FinancialHelper.getOperatingAccount()?.id ?? '';
        }
    }

    /**
     * Sort transaction by preferring trust funding (FR) before payments from trust (PT)
     * @param {Transaction} a
     * @param {Transaction} b
     * @returns {number}
     */
    static sortTransactionsByType = (a: Transaction, b: Transaction): number => {
        if (DateUtil.isSame(a.txDate, b.txDate)) {
            // We always put trust payments first
            const isTrustA = a.txType === 'FR';
            const isTrustB = b.txType === 'FR';
            if (isTrustA === isTrustB) return dateAsNumber(a.txDate) - dateAsNumber(b.txDate);
            else if (isTrustA) return -1;
            else if (isTrustB) return 1;
        }
        else return dateAsNumber(a.txDate) - dateAsNumber(b.txDate);

        return 0;
    }

    /**
     *
     * @param {Matter} matter
     * @param {Date} [asOf]
     * @return {number} - The total amount of all general trusts.
     */
    static getGeneralTrusts(matter: Matter, asOf?: Date): number {
        return matter.clients.reduce((total, clientId) => {
            return total + FinancialHelper.getTrustAmount('G', clientId, {asOf});
        }, 0);
    }

    /**
     * Get the trust amounts for the matter, client(s), and the total of all trusts.
     *
     * @param {Matter} matter
     * @param {Date} [asOf]
     * @returns {TrustAmounts}
     */
    // OWA-554: the body already guards `!matter` and returns zeros, so the param is
    // genuinely nullable — callers pass `find('matters', …)` (Matter | null).
    static getTrustAmounts(matter: Matter | null | undefined, asOf?: Date): TrustAmounts {
        if (!matter || matter instanceof Lead) return {caseTrust: 0, generalTrust: 0, totalTrust: 0};

        // Compute cache key and dependency hash
        const cacheKey = `${matter.id}:${asOf ? asOf.getTime() : 'no-asof'}`;
        const depsHash = computeTrustAmountsDepsHash(matter, asOf);

        // Check cache
        const cached = trustAmountsCache.get(cacheKey);
        if (cached && cached.depsHash === depsHash) {
            return cached.result;
        }

        // Compute the result at scale 4 — display callers round once at the
        // UI boundary. Mid-calc rounding here would compound across three
        // levels (case trust, general trust, total). See OWA-451.
        const caseTrust = FinancialHelper.getTrustAmount('M', matter.id, {asOf});
        const generalTrust = this.getGeneralTrusts(matter, asOf);
        const totalTrust = caseTrust + generalTrust;

        const result = {caseTrust, generalTrust, totalTrust};

        // Cache and return
        trustAmountsCache.set(cacheKey, {result, depsHash});
        return result;
    }

    /**
     * @param {Matter} matter
     * @param {object} [options]
     * @param {'horizontal'|'vertical'} [options.layout]
     * @param {boolean} [options.noColors]
     * @param {string} [options.label]
     * @return {JSX.Element}
     */
    static renderTrust(matter: Matter, options?: { layout?: 'horizontal' | 'vertical'; noColors?: boolean; label?: string; }): ReactElement {
        const {caseTrust, generalTrust, totalTrust} = FinancialHelper.getTrustAmounts(matter);

        const unbilled = CaseHelper.getTotalUnbilled(matter);
        const isRestricted = (matter.maxClientFunds ?? 0) > 0;

        // Set defaults for options
        if (!options) options = {
            layout: 'vertical',
            noColors: false
        };

        const nc = options.noColors ? 'nc-' : '';
        const fmt = isRestricted && totalTrust === 0
                    ? 'restricted'
                    : (totalTrust < unbilled ? 'red-rubber-band' : totalTrust < (matter.minimumBalance ?? 0) ? 'low-trust' : 'ok');
        const type = nc + fmt;
        const withPortal = (key: string): string => {
            if (isPortalUser()) return key + '_portal';
            else return key;
        }

        const title = totalTrust < unbilled
                      ? i18n.l('caseFinancialSummary', withPortal('lowTrust'))
                      : totalTrust < (matter.minimumBalance ?? 0)
                        ? i18n.l('caseFinancialSummary', withPortal('minimumBalance'), {minimumBalance: util.numberWithCommas(matter.minimumBalance ?? 0)})
                        : i18n.l('caseFinancialSummary', withPortal('trustBalance'), {trustBalance: util.numberWithCommas(totalTrust)});

        const iconColor = options.noColors ? 'darkgreen' : 'white';

        let caseTrustEl = caseTrust ? <div>
            <IconButton type={Icons.CASE} color={iconColor}/>
            <div>{util.formatCurrency(caseTrust)}</div>
        </div> : null;

        const generalTrustEl = generalTrust ? <div>
            <IconButton type={Icons.CONTACT} color={iconColor}/>
            <div>{util.formatCurrency(generalTrust)}</div>
        </div> : null;

        // Special case: no money in either trust
        if (!caseTrust && !generalTrust) {
            caseTrustEl = <div className='icon-button__no-space'>
                <div>{util.formatCurrency(0.00)}</div>
            </div>;
        }

        return <div className='revenue'>
            <Tooltip title={title}>
                <div className={`revenue-trust revenue-${type} revenue--${options.layout}`}>
                    {options.label ? <label>{options.label}</label> : ''}
                    {caseTrust ? caseTrustEl : null}
                    {generalTrust ? generalTrustEl : null}
                    {!caseTrust && !generalTrust ? caseTrustEl : null}
                    {!caseTrust && !generalTrust ? generalTrustEl : null}
                </div>
            </Tooltip>
        </div>;
    }

    /**
     * Format a label for display in a select box.
     *
     * @param {IconType} icon
     * @param {string} name
     * @param {number} balance
     * @param {string} [color]
     * @return {JSX.Element}
     */
    // OWA-554: `balance` is genuinely nullable — many call sites (e.g. the payer/contact
    // labels in PaymentCard) pass `null` to render a name-only label with no balance. The
    // body already guards with `balance !== null`, so the `number` annotation was a lie.
    static formatLabel = (icon: IconType, name: string, balance: number | null, color?: string): string | ReactElement => {
        return <div className='transactions__label'>
            <IconButton type={icon} color={color}/>
            <div className='transactions__label-name'>{name}</div>
            {balance !== null ?
             <div className='transactions__label-balance'>[${util.numberWithCommas(balance)}]</div> : null}
        </div>;
    };

    /**
     * Used only in logging.
     *
     * @param {Transaction} tx
     * @returns {{amount: number, txType: string, txDate: object}}
     */
    static getTxDetails = (tx: Transaction): { amount: number; txType: string; txDate: object; } => {
        const {txType, amount, txDate} = tx;
        return {txType, amount, txDate};
    }

    /**
     * Determine if a transaction's value should be considered a negative value.
     *
     * @param {Transaction} tr
     * @param {uuid[]} [accountIds]
     * @return {boolean}
     */
    static isNegative = (tr: Transaction, accountIds?: uuid[]): boolean => {
        // Certain codes are always negative
        if (FinancialHelper.NEGATIVE_TYPES.includes(tr.txType)) return true;
        else if (['ST', 'SA', 'RO'].includes(tr.txType)) return false;
        else return 'TT' === tr.txType && (accountIds?.includes(tr.source ?? '') ?? false);
    };

    /**
     * The signed amount one row of a finalized invoice's frozen trust snapshot (finalValues.trustAccounting)
     * added to its trust, for display. A snapshot stores the amount the transaction carried (usually
     * positive, but it can be negative) and does not keep a transfer's source, so a trust transfer's
     * direction comes from its running balance, which the snapshot computed with isNegative. Other
     * types are signed the way isNegative signs them. Zero is returned as 0, never -0. (OWA-967)
     *
     * @param {FinalTrustInfo} account - the frozen case or client trust
     * @param {number} index - the row's position in account.transactions
     * @return {number}
     */
    static getFrozenTrustRowAmount = (account: FinalTrustInfo, index: number): number => {
        const row = account.transactions[index];
        if (!row.amount) return 0;
        if (row.type !== 'TT') return FinancialHelper.NEGATIVE_TYPES.includes(row.type) ? -row.amount : row.amount;

        const previousBalance = index > 0 ? account.transactions[index - 1].runningBalance : account.beginningBalance;
        return row.runningBalance < previousBalance ? -Math.abs(row.amount) : Math.abs(row.amount);
    };

    /**
     * Take the transactions and a starting balance, then add a currentBalance field to each transaction with the balance as of that transaction.
     *
     * NOTE: This function only produces side effects!  The transactions array is modified in-place.
     *
     * @param {number} startingBalance
     * @param {Transaction[]} transactions
     * @param {uuid} id - the id of the source
     */
    static balanceTransactions = (startingBalance: number, transactions: Transaction[], id: uuid): void => {
        let runningTotal = startingBalance;

        transactions.forEach(tr => {
            runningTotal += (FinancialHelper.isNegative(tr, [id]) ? -1 : 1) * FinancialHelper.getPaymentAmount(tr, id);
            tr.currentBalance = runningTotal;
        });
    }

    /**
     * Is this transaction related to the given client trust?
     * @param {Transaction} tr
     * @param {uuid} cid - client ID for general trust
     * @returns {boolean}
     */
    static isClientTrustTransaction = (tr: Transaction, cid: uuid): boolean => {
        // Is this related to a general (client) trust?
        return (tr.sourceType === 'G' && tr.source === cid) ||
            (tr.targetType === 'G' && tr.target === cid);
    }

    /** We use a completely separate function for getting client trust info, as client trust transactions are not
     * tied to a specific invoice.
     *
     * It returns three items:
     * 1. The starting balance, which is calculated based on the balance at the end of the previous invoice.
     * 2. Transactions for this period, which is calculated based on transactions that happened after the close
     *    of the previous invoice
     * 3. The current balance, which is the previous balance plus the sum of the transactions since then.
     *
     * If no invoice is specified, we just get the current trust amount.
     *
     * @param {uuid} cid - the ID of the client
     * @param {Invoice} [invoice] - the current invoice being considered
     * @returns {ClientTrustInfo}
     */
    static getClientTrustInfo = (cid: uuid, invoice?: Invoice): ClientTrustInfo | null => {
        // Compute cache key and dependency hash
        const cacheKey = `${cid}:${invoice?.id || 'no-invoice'}`;
        const depsHash = computeClientTrustDepsHash(cid, invoice);

        // Check cache
        const cached = clientTrustCache.get(cacheKey);
        if (cached && cached.depsHash === depsHash) {
            return cached.result;
        }

        // Compute the result
        const result = FinancialHelper._computeClientTrustInfo(cid, invoice);

        // Cache and return
        clientTrustCache.set(cacheKey, { result, depsHash });
        return result;
    };

    private static _computeClientTrustInfo = (cid: uuid, invoice?: Invoice): ClientTrustInfo | null => {
        // OWA-947: a finalized invoice keeps the figures it was sent with (some have no frozen
        // trust snapshot and are computed here); drafts and the live balance (no invoice
        // given) count the starting trust once.
        const keepsSentFigures = invoice?.invoiceStatus === 'F';
        if (!invoice) {
            const invoices = store.getState().invoices
                .filter(inv => inv.clientIds.includes(cid))
                .sort((a, b) => (b.endDate?.getDate() || 0) - (a.endDate?.getDate() || 0));

            // If there are no invoices, we just add up all (applicable) trust transactions
            if (invoices.length === 0) {
                const endDate = new Date();
                // Just add up all trust transactions
                const applicableTransactions = store.getState()
                    .transactions
                    .getIndex('notDeleted') // Ignore deleted transactions
                    .filter(tr => {
                        if (tr.voidedDate || tr.refusedDate) return false; // Ignore voided/refused transactions

                        return (tr.txType !== 'ST') && // No starting trust transactions
                            FinancialHelper.isClientTrustTransaction(tr, cid) && // Matching this client's general trust
                            (tr.txDate <= endDate); // Before the end date given
                    });

                // If there is a starting trust transaction (ST), get the amount
                const startingTrustAmount = store.getState()
                    .transactions
                    .getMappingFor('txType', 'ST')
                    .find(tr => tr.status !== 'D' && tr.target === cid)?.amount ?? 0;

                // If there are applicable transactions, calculate the balance and return the ClientTrustInfo
                if (applicableTransactions.length > 0) {
                    FinancialHelper.balanceTransactions(startingTrustAmount, applicableTransactions, cid);

                    return {
                        startingBalance: startingTrustAmount,
                        endingBalance: applicableTransactions[applicableTransactions.length - 1].currentBalance ?? 0,
                        previousTransactions: [],
                        transactionsThisPeriod: applicableTransactions
                    };
                }
                else {
                    return {
                        startingBalance: startingTrustAmount,
                        endingBalance: startingTrustAmount,
                        previousTransactions: [],
                        transactionsThisPeriod: []
                    };
                }
            }
            else invoice = invoices[0];
        }

        // We can't use the normal "isFirstInvoice" call, as we need the first invoice that uses this cid
        const isFirstInvoice = FinancialHelper.isFirstInvoice(invoice, true);
        const isFirstInvoiceForMatter = FinancialHelper.isFirstInvoice(invoice, false);

        const isFinalized = !!invoice.finalizedOn;
        // OWA-946: a lead (consult) draft has no From date (as in OWA-940); ignore a saved one.
        const isLeadDraft = !!invoice.leadId && !(invoice.finalizedOn != null && invoice.invoiceStatus === 'F');
        // OWA-1463: transactions are instants and are compared with the day's bounds on the firm's
        // clock; other invoices' dates are calendar days and are compared with the local day.
        const startDate = invoice.startDate && !isLeadDraft ? firmStartOfDay(invoice.startDate) : ZERO_DATE;
        const endDate = invoice.issueDate ? firmEndOfDay(invoice.issueDate) : new Date();
        const endDay = invoice.issueDate ? DateUtil.endOfDay(invoice.issueDate) : new Date();

        // Response variables
        let startingBalance = store.getState()
            .transactions
            .getMappingFor('txType', 'ST')
            .find(tr => tr.status !== 'D' && tr.target === cid)?.amount ?? 0;

        let previousTransactions: Transaction[] = [];
        let transactionsThisPeriod: Transaction[] = [];
        let endingBalance = 0;

        // The end date of the previous invoice
        let lastEndDate: Date;

        // Get the absolute starting balance
        let initialBalance = 0;

        // We need all transactions related to THIS client trust, up to the date of this invoice
        const transactions = store.getState()
            .transactions
            .getIndex('notDeleted')
            .filter(tr => {
                return !(tr.voidedDate || tr.refusedDate) &&
                    FinancialHelper.isClientTrustTransaction(tr, cid) &&
                    tr.txDate <= endDate
            });

        // If this is the first invoice, get the initial balance
        // OWA-947: startingBalance already holds the client's starting trust (ST); adding it
        // again doubled it. Only a finalized invoice keeps that.
        if (isFirstInvoice && keepsSentFigures) {
            initialBalance = store.getState()
                .transactions
                .getMappingFor('txType', 'ST')
                .find(tr => tr.status !== 'D' && tr.target === cid)?.amount ?? 0;
            startingBalance += initialBalance;
        }

        // If this is the first invoice for this matter, then the lastEndDate is the start date.
        if (isFirstInvoiceForMatter) lastEndDate = startDate;

        /* If not the initial invoice, get the end date of the previous invoice.  Note that this call is used to get
         * client transactions for this specific invoice, so we only care about invoices with the same matterIds.
         */
        else {
            // Need to find the last end date
            const invoices = store.getState()
                .invoices
                .getMappingFor('matterId', invoice.matterIds[0])
                .filter(inv => {
                        return inv.clientIds.includes(cid) && // uses this client trust
                            inv.id !== invoice.id &&
                            (inv.invoiceStatus === 'F') &&
                            (inv.endDate <= endDay) // only before this end date
                        // (invoice.leadId
                        //  ? invoice.leadId === inv.leadId
                        //  : util.arrayMatch(invoice.matterIds, inv.matterIds)) && // matching matterId
                    }
                )
                .sort((a, b) => (b.endDate?.getTime() || 0) - (a.endDate?.getTime() || 0)); // sort descending by end date

            // OWA-946: a draft with no earlier finalized invoice for this client falls back
            // to the first-invoice window instead of dropping the client's trust activity.
            // Finalized invoices keep their prior result.
            if (invoices.length === 0 && isFinalized) return null;
            if (invoices.length === 0) lastEndDate = startDate;
            else {

            // We sorted descending, so the first one is the latest invoice before this one.
            // This doesn't apply if the last invoice was for a lead.
            lastEndDate = invoices[0].leadId
                          ? ZERO_DATE :
                          firmEndOfDay(invoices[0].issueDate);

            // Get all transactions up until the lastEndDate, over all matters, then total them up to get the initialBalance.
            // Only consider invoiced transactions to ensure the numbers in the invoices do not change.
            previousTransactions = transactions
                .filter(tr => {
                    // OWA-947: the starting trust (ST) is already in startingBalance (a finalized
                    // invoice keeps what it showed).
                    if (FinancialHelper.isClientTrustTransaction(tr, cid)) {
                        return (keepsSentFigures || tr.txType !== 'ST') && tr.txDate <= lastEndDate;
                    }

                    // Need transactions related to the client trust
                    return (tr.status !== 'D') && // not deleted
                        FinancialHelper.isTrustTransaction(tr) && // trust transaction
                        (tr.txDate <= lastEndDate) && // before the last end date
                        (FinancialHelper.isClientTrustTransaction(tr, cid) || tr.txType === 'DT') && // match client's general trust or disbursement
                        !!tr.invoiceId && // has an invoice ID (others need to be invoiced now, so current txns)
                        tr.invoiceId !== invoice.id; // and not in this invoice
                })
                .sort(FinancialHelper.sortTransactionsByDate);

            if (previousTransactions.length > 0) {
                FinancialHelper.balanceTransactions(initialBalance, previousTransactions, cid);
                startingBalance += previousTransactions[previousTransactions.length - 1]?.currentBalance ?? 0;
            }
            else startingBalance += initialBalance;
            }
        }

        // Now get any transactions since the last end date, but on or before the current end date.
        // If this invoice has been finalized, only consider transactions with invoiceIds.
        // Still considering any transactions for this client trust, regardless of the case.
        transactionsThisPeriod = transactions
            .filter(tr => {
                // If the invoice is finalized, we only care about finalized entries, EXCEPT for general trust funds,
                // which we still need to see.
                if (isFinalized && !tr.invoiceId && !FinancialHelper.isClientTrustTransaction(tr, cid)) {
                    return false;
                }
                else return (tr.status !== 'D') &&
                    (tr.txType !== 'ST') &&
                    FinancialHelper.isTrustTransaction(tr) &&
                    (tr.txDate > lastEndDate) && // Need to pick up stragglers
                    (tr.txDate <= endDate) &&
                    FinancialHelper.isClientTrustTransaction(tr, cid) &&
                    !previousTransactions.find(pt => pt.id === tr.id);
            })
            .sort(FinancialHelper.sortTransactionsByDate)
            .map(tr => tr.clone()); // Don't overwrite existing currentBalance on transactions!

        // Balance the transactions, then assign to transactionsThisPeriod
        FinancialHelper.balanceTransactions(startingBalance, transactionsThisPeriod, cid);

        endingBalance = transactionsThisPeriod[transactionsThisPeriod.length - 1]?.currentBalance ?? startingBalance;

        // Return the three items of information
        return {startingBalance, previousTransactions, transactionsThisPeriod, endingBalance};
    };

    /** We use a completely separate function for getting client trust info, as client trust transactions are not
     * tied to a specific invoice.
     *
     * It returns three items:
     * 1. The starting balance, which is calculated based on the balance at the end of the previous invoice.
     * 2. Transactions for this period, which is calculated based on transactions that happened after the close
     *    of the previous invoice
     * 3. The current balance, which is the previous balance plus the sum of the transactions since then.
     *
     * @param {uuid} mid - the ID of the matter
     * @param {Invoice} invoice - the current invoice being considered
     * @returns {MatterTrustInfo}
     */
    static getMatterTrustInfo = (mid: uuid, invoice: Invoice): MatterTrustInfo => {
        // Compute cache key and dependency hash
        const cacheKey = `${mid}:${invoice.id}`;
        const depsHash = computeMatterTrustDepsHash(mid, invoice);

        // Check cache
        const cached = matterTrustCache.get(cacheKey);
        if (cached && cached.depsHash === depsHash) {
            return cached.result;
        }

        // Compute the result
        const result = FinancialHelper._computeMatterTrustInfo(mid, invoice);

        // Cache and return
        matterTrustCache.set(cacheKey, { result, depsHash });
        return result;
    };

    private static _computeMatterTrustInfo = (mid: uuid, invoice: Invoice): MatterTrustInfo => {
        // Useful conditions
        const isFirstInvoice = FinancialHelper.isFirstInvoice(invoice);
        const isFinalized = !!invoice.finalizedOn;

        // Set dates to start/end of day. OWA-1463: transactions are instants and are compared with
        // the day's bounds on the firm's clock; other invoices' dates are calendar days and are
        // compared with the local day, as before.
        const startDate = invoice.startDate ? firmStartOfDay(invoice.startDate) : ZERO_DATE;
        const endDate = invoice.issueDate ? firmEndOfDay(invoice.issueDate) : new Date();
        const endDay = DateUtil.endOfDay(invoice.issueDate) ?? new Date();

        // Response variables
        let startingBalance: number;
        let previousTransactions: Transaction[] = [];
        let transactionsThisPeriod: Transaction[];
        let endingBalance: number;

        // The end date of the previous invoice
        let lastEndDate: Date | null = null;

        // Get the absolute starting balance
        let initialBalance = 0;

        // Earlier invoices on this matter, latest first (not needed for the first invoice)
        const invoices = isFirstInvoice ? [] : store.getState()
            .invoices
            .filter(inv => inv.endDate < endDay &&
                inv.id !== invoice.id &&
                util.arrayMatch(inv.matterIds, [mid])
            )
            .sort((a, b) => DateUtil.dateAsNumber(b.endDate) - DateUtil.dateAsNumber(a.endDate)); // sort descending by end date

        // If this is the first invoice, get the initial balance ([ST]starting balance).
        // OWA-946: also when no earlier invoice ends before this one (e.g. two drafts with
        // the same end date); reading invoices[0] then crashed.
        if (isFirstInvoice || invoices.length === 0) {
            initialBalance = store.getState()
                .transactions
                .getMappingFor('txType', 'ST')
                .find(tr => tr.status !== 'D' && tr.target === mid)?.amount ?? 0;

            startingBalance = initialBalance;
        }
        // If not the initial invoice, get the end date of the previous invoice
        else {
            lastEndDate = firmEndOfDay(invoices[0].issueDate);

            // Get all transactions up until the lastEndDate, then total them up to get the initialBalance.
            // For a finalized invoice, the invoiceId must match the current invoiceId!
            previousTransactions = store.getState()
                .transactions
                .getIndex('notDeleted')
                .filter(tr => {
                    if (tr.voidedDate || tr.refusedDate) return false;

                    if (isFinalized) return invoices.find(inv => inv.transactionIds.includes(tr.id)) &&
                        FinancialHelper.isTrustTransaction(tr) &&
                        FinancialHelper.isRelatedMatter(mid, tr)
                        //tr.txType !== 'ST'; // WHY WAS THIS HERE?

                    // Need transactions related to this matter trust
                    else return FinancialHelper.isTrustTransaction(tr) &&
                        !!tr.invoiceId && // must have been invoiced
                        (tr.txDate <= lastEndDate!) && // must be before the last end date
                        FinancialHelper.isRelatedMatter(mid, tr) &&
                        tr.invoiceId !== invoice.id; // and not in this invoice

                })
                .filter(existsAndNotDeletedFilter)
                .sort(FinancialHelper.sortTransactionsByDate);

            if (previousTransactions.length > 0) {
                FinancialHelper.balanceTransactions(initialBalance, previousTransactions, mid);
                startingBalance = previousTransactions[previousTransactions.length - 1]?.currentBalance ?? 0;
            }
            else startingBalance = initialBalance;
        }

        // Now get any transactions since the last end date, but on or before the current end date
        transactionsThisPeriod = store.getState()
            .transactions
            .filter(tr => {
                if (!!tr.voidedDate || !!tr.refusedDate) return false;

                // If this is a finalized invoice, only return transactions with this invoiceId
                if (isFinalized) {
                    return FinancialHelper.isTrustTransaction(tr) && // trust types only
                        (tr.txType !== 'ST') &&  // no starting transactions
                        FinancialHelper.isRelatedMatter(mid, tr) &&
                        invoice.transactionIds.includes(tr.id);
                }

                // Need transactions related to the client trust
                else {
                    // OWA-1463: on or after the period's first instant, as isInPeriod counts it
                    return (tr.txDate >= startDate) && // after start date (usually null)
                        (tr.txDate <= endDate) &&  // before or on end date
                        !tr.invoiceId && // Not connected to an existing invoice
                        (tr.txType !== 'ST') &&  // no starting transactions
                        FinancialHelper.isTrustTransaction(tr) && // trust types only
                        FinancialHelper.isRelatedMatter(mid, tr);
                } // not deleted
            })
            .filter(existsAndNotDeletedFilter)
            .sort(FinancialHelper.sortTransactionsByDate)
            .map(tr => tr.clone()); // Don't overwrite existing currentBalance on transactions!

        // Balance the transactions, then assign to transactionsThisPeriod
        FinancialHelper.balanceTransactions(startingBalance, transactionsThisPeriod, mid);

        // Ending balance is the current balance of the last transaction in this period
        endingBalance = transactionsThisPeriod[transactionsThisPeriod.length - 1]?.currentBalance ?? startingBalance;

        // Return the three items of information
        return {startingBalance, transactionsThisPeriod, endingBalance};
    };

    /**
     * Get a list of invoice date ranges for all known invoices, draft or finalized.
     * Sort result
     * @param {uuid} matterId
     * @returns {InvoiceDateRange[]}
     */
    static getInvoiceDateRanges = (matterId: uuid): InvoiceDateRange[] => {
        const invoices = FinancialHelper.getInvoices(matterId);
        const dateRanges: InvoiceDateRange[] = [];

        for (let i = 0; i < invoices.length; i++) {
            const invoice = invoices[i];

            if (i === 0) dateRanges.push({
                finalized: !!invoice.finalizedOn,
                startDate: invoice.startDate,
                endDate: DateUtil.endOfDay(invoice.endDate),
                invoice
            });
            else dateRanges.push({
                finalized: !!invoice.finalizedOn,
                startDate: invoice.startDate || invoices[i - 1].endDate,
                endDate: DateUtil.endOfDay(invoice.endDate),
                invoice
            });
        }

        return dateRanges;
    };

    /**
     * Find invoices that could be associated with a given transaction. Used when a transaction loses its invoiceId.
     *
     * Note that for a trust => trust transaction, where both trusts are client trusts, no invoice should be returned.
     * If the transaction is associated with a particular matter, only that matter's invoices should be returned.
     * If the transaction is a trust transfer to/from a general trust to/from a matter trust, only that matter's invoices should be returned.
     */
    static getCandidateInvoicesForTransaction = (tr: Transaction): InvoiceDateRange[] => {
        const matterId = tr.matterIds.length > 0
                         ? tr.matterIds[0] || tr.matterIds[1]
                         : tr.sourceType === 'M' ? tr.source
                                                 : tr.targetType === 'M' ? tr.target
                                                                         : null;

        if (matterId) return FinancialHelper
            .getInvoiceDateRanges(matterId)
            .filter(dr => dr.finalized && dr.startDate && isBetween(tr.txDate, dr.startDate, dr.endDate));
        else return [];
    }

    /**
     * Get transactions that have not been invoiced and do not appear on any draft invoice.
     *
     * @param {uuid[]} clientIds
     * @param {uuid[]} matterIds
     * @param {Date} [startDate]
     * @param {Date} [endDate]
     * @return {Transaction[]}
     */
    static getUnbilledTransactions = (clientIds: uuid[], matterIds: uuid[], startDate: Date, endDate: Date): Transaction[] => {
        return FinancialHelper.getTransactions(clientIds, matterIds, startDate, endDate)
            .filter(tx => !tx.invoiceId)
            .filter(tx => endDate ? DateUtil.isBetween(tx.txDate, startDate, endDate) : true);
    }

    /**
     * Get transactions between the given date range, excluding any transactions belonging to a finalized invoice.
     * clientIds are required, but matterIds can be null or an empty array.  If provided, this function will
     * filter on those matterIds.
     *
     * @param {uuid[]} clientIds
     * @param {uuid[]} matterIds
     * @param {Date} [startDate]
     * @param {Date} [endDate]
     * @return {Transaction[]}
     */
    static getTransactions = (clientIds: uuid[], matterIds: uuid[], startDate?: Date, endDate?: Date): Transaction[] => {
        const _startDate = startDate ? DateUtil.startOfDay(startDate) : 0;
        const _endDate = endDate ? DateUtil.endOfDay(endDate) : new Date();

        if (matterIds?.length > 0) return store.getState()
            .transactions
            .filter(tr => {
                return (util.arrayMatch(tr.clientIds, clientIds)) &&
                    (tr.txDate >= _startDate) &&
                    (tr.txDate <= _endDate) &&
                    ((tr.sourceType === 'C' && tr.txType === 'PC') || tr.sourceType === 'M' || tr.targetType === 'M') &&
                    (clientIds.includes(tr.source as uuid) || matterIds.includes(tr.source as uuid) || matterIds.includes(tr.target as uuid)) &&

                    // Make sure this transaction has not been included on an other invoice!
                    !store.getState().invoices.some(invoice => invoice.transactionIds.includes(tr.id));
            })
            .filter(existsAndNotDeletedFilter);
        else return store.getState()
            .transactions
            .getIndex('notDeleted')
            .filter(tr => {
                return (util.arrayMatch(tr.clientIds, clientIds)) &&
                    (tr.txDate >= _startDate) &&
                    (tr.txDate <= _endDate) &&
                    (tr.sourceType === 'G' || tr.targetType === 'G') &&
                    (clientIds.includes(tr.source as uuid) || clientIds.includes(tr.target as uuid)) &&

                    // Make sure this transaction has not been included on another invoice!
                    !store.getState().invoices.some(invoice => invoice.transactionIds.includes(tr.id));
            })
            .filter(existsAndNotDeletedFilter);
    }

    /**
     * Get all transactions for an invoice.  This includes:
     * - Starting trust amounts (ST)
     * - Starting account amounts (SA)
     * - Transactions for each client trust
     * - Transactions for each matter trust
     *
     * Make sure to filter out duplicates! For example, a TT between a client and matter trust.
     *
     * @param {Invoice} invoice
     * @param {boolean} [ignoreStartDate]
     * @return {Transaction[]}
     */
    static getTransactionsForInvoice = (invoice: Invoice, ignoreStartDate?: boolean): Transaction[] => {
        // Compute cache key and dependency hash (reuse invoice deps hash)
        const cacheKey = `${invoice.id}:${ignoreStartDate ? 'ignoreStart' : 'normal'}`;
        const depsHash = getInvoiceDepsHash(invoice);
        const isFinalized = invoice.finalizedOn != null && invoice.invoiceStatus === 'F';

        // Check cache - stores IDs, resolve to current objects on hit
        const cached = transactionsCache.get(cacheKey);
        if (cached && cached.depsHash === depsHash) {
            const transactions = store.getState().transactions;
            // For finalized invoices, always use invoice.transactionIds as the source of truth.
            // The cached resultIds may be incomplete if transactions weren't loaded when first computed.
            const sourceIds = isFinalized ? (invoice.transactionIds || []) : cached.resultIds;
            return sourceIds
                .map(id => transactions.get(id))
                .filter((t): t is Transaction => t != null && t.status !== 'D');
        }

        // Compute the result
        const result = FinancialHelper._computeTransactionsForInvoice(invoice, ignoreStartDate);

        // Cache IDs only (not object references) to allow GC of old versions
        transactionsCache.set(cacheKey, { resultIds: result.map(t => t.id), depsHash });
        return result;
    };

    private static _computeTransactionsForInvoice = (invoice: Invoice, ignoreStartDate?: boolean): Transaction[] => {
        const results: Transaction[] = [];

        const isFirstInvoice = FinancialHelper.isFirstInvoice(invoice);
        // Use the same finalization check as getInvoiceDepsHash to ensure cache consistency
        const isFinalized = invoice.finalizedOn != null && invoice.invoiceStatus === 'F';

        if (isFirstInvoice) {
            // Get any SA transactions (or are those returned from other functions?)
            const sa = store.getState()
                .transactions
                .getMappingFor('txType', 'SA')
                .filter(existsAndNotDeletedFilter)
                .find(tr => invoice.matterIds.includes(tr.target as uuid));
            if (sa) results.push(sa);
        }

        // Get start trust transaction for each client
        invoice.clientIds.forEach((cid: uuid) => {
            if (isFirstInvoice) {
                const st = store.getState()
                    .transactions
                    .getMappingFor('txType', 'ST')
                    .filter(existsAndNotDeletedFilter)
                    .find(tr => tr.target === cid);
                if (st) results.push(st);
            }

            const cti = FinancialHelper.getClientTrustInfo(cid, invoice);
            if (cti) {
                cti.transactionsThisPeriod
                    .forEach(tr => {
                        if (!results.find(tx => tx.id === tr.id)) results.push(tr);
                    });
            }
        });

        // Get transactions for each matter
        invoice.matterIds.forEach(mid => {
            if (isFirstInvoice) {
                const st = store.getState()
                    .transactions
                    .getMappingFor('txType', 'ST')
                    .filter(existsAndNotDeletedFilter)
                    .find(tr => tr.target === mid);
                if (st) results.push(st);
            }

            const cti = FinancialHelper.getMatterTrustInfo(mid, invoice);
            if (cti) {
                cti.transactionsThisPeriod
                    .forEach(tr => {
                        if (!results.find(tx => tx.id === tr.id)) results.push(tr);
                    });
            }
        });

        // OWA-946: a draft never re-bills a transaction a finalized invoice already lists,
        // even if that transaction's invoiceId back-pointer failed to save.
        const invoicedElsewhere = isFinalized ? null : FinancialHelper.getInvoicedTransactionIds(invoice.id);
        const zone = firmTimeZone();

        // Get non-trust, non-SA transactions ('BA', 'PC', 'RO')
        store.getState().transactions
            .getIndex('notTrustTypes')
            .filter(tr => {
                if (tr.voidedDate || tr.refusedDate) return false;

                // Only non-trust
                if (tr.txType !== 'SA') {
                    if (isFinalized && tr.invoiceId === invoice.id) results.push(tr);

                    // Check date ranges and previous invoice status
                    // OWA-1463: a transaction's calendar day is the one on the firm's clock.
                    else if (
                        // OWA-940: a lead (consult) draft has no From date; ignore any saved one.
                        // Finalized invoices keep their exact prior selection.
                        (ignoreStartDate || (!isFinalized && !!invoice.leadId) || !invoice.startDate || isOnOrAfterFirmDay(tr.txDate, invoice.startDate, zone)) &&
                        // OWA-940: a lead (consult) draft counts every one of the lead's unbilled
                        // payments, whatever their date.
                        ((!isFinalized && !!invoice.leadId) || isOnOrBeforeFirmDay(tr.txDate, invoice.issueDate, zone)) &&
                        !tr.invoiceId && !invoicedElsewhere?.has(tr.id) && (
                            util.arrayMatch(invoice.matterIds, tr.matterIds) || // Matter PC and RO
                            (invoice.leadId === tr.entityId) && tr.entityType === 'lead' || // Leads PC
                            invoice.matterIds.includes(tr.target as uuid) // BA
                        )
                    ) results.push(tr);
                }
            })
            .filter(existsAndNotDeletedFilter);

        return results.sort((a, b) => dateAsNumber(a.txDate) - dateAsNumber(b.txDate));
    };

    /**
     * Get tasks for the given invoice.  Return with proper positioning, based on the invoice.taskPositions, if available.
     *
     * @param {Invoice} invoice
     * @return {Task[]}
     */
    static getTasksForInvoice = (invoice: Invoice): Task[] => {
        return FinancialHelper._computeTasksForInvoice(invoice);
    };

    private static _computeTasksForInvoice = (invoice: Invoice): Task[] => {
        // Use the same finalization check as getInvoiceDepsHash to ensure cache consistency
        const isFinalized = invoice.finalizedOn != null && invoice.invoiceStatus === 'F';

        const tasks = isFinalized
                      ? store.getState().tasks.getMappingFor('invoiceId', invoice.id)
                      : invoice.leadId
                        ? store.getState().tasks.getMappingFor('leadId', invoice.leadId)
                        : store.getState().tasks.getMappingsFor('matterId', invoice.matterIds);

        let result: Task[];

        // First get a list of tasks. Finalized tasks will have the invoiceId stamped on them.
        if (isFinalized) result = tasks.filter(util.existsAndNotDeletedFilter);

        // Non-invoiced tasks will have to be found based on time ranges and other criteria
        else {
            const zone = firmTimeZone();
            result = tasks.filter(task => {
                return (
                    // Check for billable and completed
                    task.billable && !!task.completionDate &&

                    // Make sure this task hasn't already been included on another invoice
                    (!task.invoiceId || task.invoiceId === invoice.id) &&

                    // Check the tieIns, we need to look for both matter and leads, as we can bill a lead
                    task.tieIns.some(ti => invoice.matterIds?.includes(ti.id) || invoice.leadId === ti.id) &&

                    // Task completion date must be within the invoice date range (comparing calendar dates only)
                    // OWA-1463: the completion's calendar day is the one on the firm's clock.
                    // A null startDate means "no lower bound" — include all tasks up to the endDate
                    // OWA-940: a lead (consult) draft has no From date; ignore any saved one.
                    (!!invoice.leadId || !invoice.startDate || isOnOrAfterFirmDay(task.completionDate, invoice.startDate, zone)) &&
                    isOnOrBeforeFirmDay(task.completionDate, invoice.endDate, zone)
                );
            }).filter(util.existsAndNotDeletedFilter);
        }

        // Now sort based on the taskPositions in the invoice, if available.  Note that if the tasks in the
        // taskPositions array do not match those in the invoice's taskIds, then we need to do a little work.
        if (Array.isArray(invoice.taskPositions) && invoice.taskPositions.length > 0) {
            const propTasks = result;

            // OK, there is a prior taskPositions list.  Let's see if the list of tasks is accurate.
            if (util.arrayMatch(invoice.taskIds, invoice.taskPositions)) {
                // Great, all tasks accounted for! Use the existing order
                return invoice.taskPositions
                    .map(tid => propTasks.find(t => t.id === tid))
                    .filter(util.existsFilter);
            }

            // Hm, a new task got in here (or one disappeared).
            else {
                // Need to identify tasks not in taskPositions, and position them as best as possible
                const taskIdsFromPositions = invoice.taskPositions;
                const taskIdsFromInvoice = invoice.finalizedOn ? invoice.taskIds : result.map(t => t.id);
                const tasksFromPositions = invoice.taskPositions
                    .filter(tid => taskIdsFromInvoice.includes(tid))
                    .map(tid => propTasks.find(t => t.id === tid))
                    .filter(util.existsFilter);

                const {onlyInTarget} = util.arrayExclusions(taskIdsFromPositions, taskIdsFromInvoice);

                // For items only in target (invoice.taskIds), we need to situate them immediately after the first task
                // with an earlier completionDate
                onlyInTarget.forEach(tid => {
                    // Get the task from redux
                    const task = propTasks.find(t => t.id === tid);
                    if (task) {
                        // Find first in state with a lower completionDate
                        const index = tasksFromPositions.findIndex(t => dateAsNumber(t.completionDate ?? undefined) > dateAsNumber(task.completionDate ?? undefined));
                        tasksFromPositions.splice(index, 0, task);
                    }
                });
                return tasksFromPositions;
            }
        }
        // No map, just use completion dates
        else {
            return result.sort((a, b) => dateAsNumber(a.completionDate ?? undefined) - dateAsNumber(b.completionDate ?? undefined));
        }
    };

    /**
     * Get expenses for the given invoice
     *
     * @param {Invoice} invoice
     * @return {Expense[]}
     */
    static getExpensesForInvoice = (invoice: Invoice): Expense[] => {
        // Compute cache key and dependency hash
        const cacheKey = invoice.id;
        const depsHash = getInvoiceDepsHash(invoice);
        const isFinalized = invoice.finalizedOn != null && invoice.invoiceStatus === 'F';

        // Check cache - stores IDs, resolve to current objects on hit
        const cached = expensesCache.get(cacheKey);
        if (cached && cached.depsHash === depsHash) {
            const expenses = store.getState().expenses;
            // For finalized invoices, always use invoice.expenseIds as the source of truth.
            // The cached resultIds may be incomplete if expenses weren't loaded when first computed.
            const sourceIds = isFinalized ? (invoice.expenseIds || []) : cached.resultIds;
            return sourceIds
                .map(id => expenses.get(id))
                .filter((e): e is Expense => e != null && e.status !== 'D');
        }

        // Compute the result
        const result = FinancialHelper._computeExpensesForInvoice(invoice);

        // Cache IDs only (not object references) to allow GC of old versions
        expensesCache.set(cacheKey, { resultIds: result.map(e => e.id), depsHash });
        return result;
    };

    private static _computeExpensesForInvoice = (invoice: Invoice): Expense[] => {
        // Use the same finalization check as getInvoiceDepsHash to ensure cache consistency
        const isFinalized = invoice.finalizedOn != null && invoice.invoiceStatus === 'F';

        const invoices = store.getState().invoices;
        const expenses = store.getState().expenses;

        if (isFinalized) return invoice.expenseIds
            .map(eid => expenses.get(eid))
            .filter(util.existsAndNotDeletedFilter);

        const zone = firmTimeZone();
        return expenses
            .getMappingsFor('matterId', invoice.matterIds)
            .filter(exp => {
                // An expense has the matterId stored in entityId where the entityType is 'matter' (which it always is)...
                return (
                    // Must be reimbursable
                    exp.reimbursable &&

                    // Check if this is the right matter
                    invoice.matterIds.includes(exp.entityId) &&

                    // Can't be hidden
                    (exp.excludeFromInvoice === false) &&

                    // Compare calendar dates; OWA-1463: the expense's day is the one on the firm's clock.
                    // A null startDate means "no lower bound" — include all expenses up to the endDate
                    (!invoice.startDate || isOnOrAfterFirmDay(exp.expenseDate, invoice.startDate, zone)) &&
                    isOnOrBeforeFirmDay(exp.expenseDate, invoice.endDate, zone) &&

                    // Make sure this hasn't been included on another invoice
                    !invoices.some(inv => inv.id !== invoice.id && inv.expenseIds.includes(exp.id))
                );
            })
            .filter(util.existsFilter)
            .sort((a, b) => dateAsNumber(a.expenseDate) - dateAsNumber(b.expenseDate));
    };

    /**
     * Get the first day after the last invoice period.
     *
     * @param {Invoice} invoice
     * @returns {Date}
     */
    static lastInvoiceEnd = (invoice: Invoice): Date => {
        const invoices = FinancialHelper.getInvoices(invoice.matterIds[0], 'desc')
            .filter(inv => inv.id !== invoice.id && inv.invoiceStatus === 'F' && !inv.leadId);

        // OWA-1463: the first instant of the next day on the firm's clock.
        if (invoices.length > 0) return firmStartOfDay(DateUtil.add(invoices[0].endDate, 1, 'day'));
        else return ZERO_DATE;
    }

    /**
     * Find flat fees and flat fee milestones that were completed in the invoice period.
     *
     * There is an issue here caused by the relationship between flat fees and their milestones:
     * - A flat fee's milestones may not add up to the total amount of the flat fee.  This may be the case if there
     *   is one part of the flat fee that they can collect for early, but the rest has to be earned when delivered.
     *   In this case the milestone would have its own completion date, and would be paid out in that invoice cycle;
     *   the rest of the flat fee would be paid out when the flat fee's completion date is marked. The amount paid out
     *   THEN is the amount of the parent flat fee MINUS the amount of any milestones.
     *
     * We therefore need to check a flat fee's milestones before determining how much to pay out.
     *
     * Start by looking at all flat fees for the invoice's matter(s), then:
     * 1. See if the flatFee has a completion date in the time range. If so,
     *    1a. Add up the total of previously billed milestones (they have an invoiceId !== this invoice's id)
     *    2b. Return the amount of the flat fee minus any previously billed milestones.
     * 2. If the flatFee's completion date is in the past, nothing to do here
     * 3. If the flatFee does not have a completion date, look for any milestones with a completion date in this
     *    invoice period and return the sum of those amounts.
     *
     * The result should contain any flat fee that had work done, whether the entire flat fee was earned or not.
     * We tag every such flat fee with an 'earnedThisPeriod' amount that will be shown to the client.
     *
     * @param {Invoice} invoice
     * @return {{earnedAmount: number, flatFees: FlatFee[], flatFeeMilestones: FlatFeeMilestone[]}}
     */
    static getFlatFeesForInvoice = (invoice: Invoice): { earnedAmount: number; flatFees: FlatFee[]; flatFeeMilestones: FlatFeeMilestone[]; } => {
        // Invoice status
        const isFinalized = !!invoice.finalizedOn;

        /**
         * OWA-726: milestones must be looked up by their PARENT FLAT FEE, not by matter.
         *
         * A milestone is stored with entityType 'flatFee' and entityId = the parent flat
         * fee's id (see FlatFeeMilestoneCard). The 'matterId' map only indexes rows whose
         * entityType is 'matter', so every real milestone lands under a null key and a
         * getMappingFor('matterId', ...) lookup returns [] — always. That silently zeroed
         * every milestone-billed invoice: earnedAmount never accumulated, the ids were
         * never associated at finalize, and they were dropped from snapshots and PDFs.
         * Worse, the `milestones.length === 0` branch below then treats a milestone-backed
         * flat fee as milestone-less and can charge the full parent amount again.
         *
         * Also use the PLURAL getMappingsFor over the whole matterIds array: keying on
         * matterIds[0] alone made a multi-matter invoice ignore every matter after the
         * first. Both regressions arrived together in 4fdf66dc3 (2025-11-10), which swapped
         * a full-index scan for these mapping lookups.
         */
        const flatFees = store.getState().flatFees.getMappingsFor('matterId', invoice.matterIds);
        const flatFeeMilestones = store.getState().flatFeeMilestones.getMappingsFor(
            'flatFeeId',
            flatFees.map(ff => ff.id),
        );

        const startDate = isFinalized
                          ? ZERO_DATE
                          : invoice.startDate ? firmStartOfDay(invoice.startDate) : FinancialHelper.lastInvoiceEnd(invoice);

        // OWA-1463: completion dates are instants, compared with the period's firm-clock bounds.
        const endDate = invoice.endDate ? firmEndOfDay(invoice.endDate) : new Date();

        const _flatFees: FlatFee[] = [];
        const _flatFeeMilestones: FlatFeeMilestone[] = [];

        /**
         * Method to check the dates of a flat fee (or flat fee milestone) to be in this invoice range.
         *
         * @param {FlatFee|FlatFeeMilestone} ff
         * @returns {boolean}
         */
        const checkDates = (ff: FlatFee | FlatFeeMilestone): boolean => {
            return ff.earned &&
                !!ff.completionDate &&
                ff.completionDate >= startDate &&
                ff.completionDate <= endDate;
        }

        const checkDatesAfter = (ff: FlatFee | FlatFeeMilestone): boolean => {
            return ff.earned &&
                !!ff.completionDate &&
                ff.completionDate > endDate;
        }

        /**
         * Method to Check if the flat fee is not associated with an invoice (uninvoiced), or is associated
         * with the present invoice.
         */
        const inInvoicePeriod = isFinalized ? (ff: FlatFee | FlatFeeMilestone) => ff.invoiceId === invoice.id
                                            : (ff: FlatFee | FlatFeeMilestone) => ff.earned && !ff.invoiceId && checkDates(ff);

        const isAfterInvoicePeriod = isFinalized ? (ff: FlatFee | FlatFeeMilestone) => ff.invoiceId === invoice.id
                                                 : (ff: FlatFee | FlatFeeMilestone) => ff.earned && !ff.invoiceId && checkDatesAfter(ff);

        /*
		 * Example:
		 *
		 * ff1: $1800 earned this period
		 *   ffm1.1: $1000 earned before this period
		 *   ffm1.2: $800 earned this invoice period
		 *
		 * ff2: $2000 earned this invoice period (no milestones)
		 *
		 * ff3: $3000 not earned
		 *   ffm3.1: $2000 earned this period
		 *   ffm3.2: $1000 earned after this period
		 *
		 * ff4: $2000 marked earned
		 *   ffm4.1: $500 marked previously earned
		 *   ffm4.2: $800 marked earned in this period
		 *   ($700 was general pot, but marked earned this period in ff4)
		 *
		 * Results:
		 * - ffm1.2 added to total (only needed to look at milestones)
		 * - ff2 added to total (no milestones)
		 * - ffm3.1 added to total (only needed to look at milestones)
		 * - ffm4 added $700 to total $($500 marked earned previously, ffm4.2 earned separately)
		 * - ffm4.2 added to total
		 */

        // Counter for the earned amount this period
        let earnedAmount = 0;

        // Check each flat fee to see if it has been earned this period, and if so, how much was earned. This will
        // be based on how much of the flat fee's milestones have been earned and invoiced already.
        flatFees
            // Check for flat fees of type "matter". Flat fees of type "caseType" are templates.
            // Then check to see if they are related to the matters in the invoice.
            .filter(ff => ff.entityType === 'matter' && invoice.matterIds.includes(ff.entityId))
            .map(ff => ff.clone())
            .forEach(ff => {
                // Start at 0
                ff.earnedThisPeriod = 0;

                // Reset the milestone totals for this flat fee
                let newMilestoneTotal = 0;
                let oldMilestonesTotal = 0;

                // Check if this flat fee was earned/completed in this invoice period.  This requires us to know
                // if the invoice has been finalized, as finalized invoices only care if the flat fee was
                // earned/completed AND invoiced during the invoice period.
                const flatFeeCompletedThisPeriod = checkDates(ff);
                const flatFeeInvoicedThisPeriod = inInvoicePeriod(ff);
                const flatFeeInvoicedAfterThisPeriod = isAfterInvoicePeriod(ff);

                // Check this flat fee's milestones for anything earned this period
                const milestones = flatFeeMilestones.filter(ffm => ffm.status !== 'D' && ffm.entityId === ff.id)
                    .map(ffm => ffm.clone());

                milestones.filter(ffm => ffm.status !== 'D' && ffm.entityId === ff.id)
                    .map(ffm => ffm.clone())
                    .forEach(ffm => {
                        // Start at 0
                        // ffm.earnedThisPeriod = 0;

                        // Check milestone for completion and invoicing status.
                        const milestoneCompletedThisPeriod = checkDates(ffm);
                        const milestoneInvoicedThisPeriod = inInvoicePeriod(ffm);
                        const milestoneInvoicedAfterThisPeriod = isAfterInvoicePeriod(ffm);

                        // If the milestone was earned and invoiced this period, add it to the total
                        if (milestoneCompletedThisPeriod) {
                            // If in invoicing period, add to the amount
                            if (milestoneInvoicedThisPeriod) {
                                // Now we add to the
                                ffm.earnedThisPeriod = ffm.amount;
                                newMilestoneTotal += ffm.amount;
                                earnedAmount += ffm.amount;
                            }
                            else ffm.earnedThisPeriod = 0;

                            _flatFeeMilestones.push(ffm);
                        }

                        // Otherwise it was already earned in a previous (or later) time
                        else {
                            if (milestoneInvoicedAfterThisPeriod) ffm.earnedAfterThisPeriod = true;

                            if (ffm.earned &&
                                // ffm.completionDate < endDate &&
                                ffm.invoiceId !== invoice.id
                            ) {
                                ffm.earnedThisPeriod = 0;
                                _flatFeeMilestones.push(ffm);
                                oldMilestonesTotal += ffm.amount;
                            }
                        }
                    });

                // If the flat fee was marked earned this period we at least add it to the _flatFees
                if (flatFeeCompletedThisPeriod && milestones.length === 0) {
                    // If it was also invoiced this period, we need to add to the totals
                    if (flatFeeInvoicedThisPeriod) {
                        ff.earnedThisPeriod = ff.amount - oldMilestonesTotal;
                        earnedAmount += ff.earnedThisPeriod;
                    }
                    else ff.earnedThisPeriod = 0;

                    _flatFees.push(ff);
                }

                else if (flatFeeInvoicedAfterThisPeriod) {
                    ff.earnedAfterThisPeriod = true;
                }

                    // If the flat fee was not not earned this period, we should look at it's milestones
                // TODO: Is this necessary/correct?
                else if (newMilestoneTotal > 0) {
                    ff.earnedThisPeriod = newMilestoneTotal;
                }
            });

        // Need to know:
        // - which flatFees and milestones were completed in the invoice period
        // - for each flatFee or milestone, what amount is earned (this is where it gets tricky)
        // - total earned this invoice period
        return {
            earnedAmount,
            flatFees: _flatFees, // This will be ONLY flat fees with no milestones at all
            flatFeeMilestones: _flatFeeMilestones
        };
    };

    /**
     * Get the next invoice number.
     *
     * @param {string} [useAsLatest]
     * @returns {string}
     */
    static findNextInvoiceNumber = (useAsLatest?: string): string => {
        let max: number;

        if (useAsLatest) {
            max = Number.parseInt(useAsLatest);
        }
        else {
            const invoiceNumbers: number[] = store.getState()
                .invoices
                .map(invoice => Number.parseInt(invoice.invoiceNumber))
                .filter((num: number) => !isNaN(num));

            max = invoiceNumbers.length > 0 ? Math.max(...invoiceNumbers) : 0;
        }

        if (isNaN(max)) max = 0;

        return `${max + 1}`.padStart(6, '0');
    };

    /**
     * Total transaction amounts
     *
     * @param {Transaction[]} transactions
     */
    static sumTransactions = (transactions: Transaction[]): number => {
        return transactions.map(tx => tx.amount).reduce((tot, cur) => tot + cur, 0);
    }

    /**
     * Get the sum of the tasks, from task ids.
     *
     * @param {Task[]} tasks
     * @returns {number}
     */
    static getLaborTotal = (tasks: Task[]): number => {
        return MathUtils.sum(tasks.filter(util.existsFilter).map(t => {
            if (t.flatFeeBucket || t.hideFromInvoice || t.noCharge) return 0;
            else return t.isCostOverridden ? t.costOverride : getCost(t);
        }));
    };

    /**
     * Get the sum of the expenses, from expense ids.
     *
     * @param {uuid[]} expenseIds
     * @returns {number}
     */
    static getExpenseTotal = (expenseIds: uuid[]): number => {
        return MathUtils.sum(store.getState().expenses.filter(e => expenseIds.includes(e.id)).map(e => e.amountPaid));
    };

    /**
     * Get the sum of the transactions, from transaction ids.
     *
     * @param {uuid[]} txIds
     * @returns {number}
     */
    static getTransactionTotal = (txIds: uuid[]): number => {
        return MathUtils.sum(store.getState()
            .transactions
            .getIndex('notDeleted')
            .filter(tx => {
                if (tx.voidedDate || tx.refusedDate) return false;
                else return txIds.includes(tx.id)
            })
            .map(tx => tx.amount));
    };

    /** Refresh the persisted draft totals without mutating the editor or a finalized invoice. */
    static withCurrentDraftTotals = (invoice: Invoice): Invoice => {
        if (invoice.invoiceStatus !== 'D') return invoice;

        const draft = invoice.clone();
        const info = FinancialHelper.getInvoiceInfo(draft)!;
        draft.totalBilled = info.currentInvoiceAmount;
        draft.totalDue = info.amountDue;
        draft.amountPaid = info.paymentTotal;
        return draft;
    };

    /**
     * Generate the total amount of an invoice on the fly
     *
     * @param {Invoice} invoice
     * @return {InvoiceInfo|null}
     */
    static getInvoiceInfo = (invoice: Invoice, ignoreFinalValues?: boolean): InvoiceInfo | null => {
        if (!invoice) {
            console.warn('Got null invoice');
            return null;
        }

        // Compute cache key and dependency hash
        const cacheKey = `${invoice.id}${ignoreFinalValues ? ':old' : ''}`;
        const depsHash = getInvoiceDepsHash(invoice);

        // Check cache
        const cached = invoiceInfoCache.get(cacheKey);
        if (cached && cached.depsHash === depsHash) {
            return cached.result;
        }

        // Compute the result
        const result = FinancialHelper._computeInvoiceInfo(invoice, ignoreFinalValues);

        // Cache and return
        invoiceInfoCache.set(cacheKey, { result, depsHash });
        return result;
    };

    private static _computeInvoiceInfo = (invoice: Invoice, ignoreFinalValues?: boolean): InvoiceInfo | null => {
        // If this invoice is finalized and has a frozen snapshot, use that instead of live data.
        const fv = invoice.finalValues as FinalValues | null;
        if (!ignoreFinalValues && invoice.invoiceStatus === 'F' && fv?.tasks) {
            return FinancialHelper._buildInvoiceInfoFromFinalValues(invoice, fv);
        }

        // Everything in an invoice is based on tasks, expenses and transactions, so get them first.
        const tasks = FinancialHelper.getTasksForInvoice(invoice).filter(util.existsFilter);
        const expenses = FinancialHelper.getExpensesForInvoice(invoice).filter(util.existsFilter);
        const transactions: Transaction[] = FinancialHelper.getTransactionsForInvoice(invoice)
            .filter(tx => {
                if (invoice.leadId) return tx.entityId === invoice.leadId;
                else return util.arrayMatch(tx.matterIds, invoice.matterIds)
            })
            .filter(util.existsFilter);

        // Calculate simple totals
        const taskTotal = FinancialHelper.getLaborTotal(tasks);
        const expenseTotal = MathUtils.sum(expenses.map(e => e.quantity * e.cost));

        // Transactions are sliced and diced in many ways.  We handle the most common use cases here

        // The transactionTotal is a sum of all transactions that affect the account balance
        const transactionTotal = MathUtils.sum(transactions.map(tx => {
            // If there is a paymentDistribution, this is an FR transaction and is always positive
            if (tx.paymentDistribution?.length > 0) {
                // Sum up the portions of the payment we care about
                return MathUtils.sum(tx.paymentDistribution
                    .filter(pd => invoice.matterIds.includes(pd.matterId))
                    .map(pd => pd.amount));
            }
            // Otherwise we have to check to see if the transaction is positive or negative in terms of account balance
            else return (FinancialHelper.isNegative(tx, [...invoice.clientIds, invoice.entityId, ...invoice.matterIds]) ? -1 : 1) * tx.amount;
        }));

        const flatFeeInfo = FinancialHelper.getFlatFeesForInvoice(invoice);

        const flatFeeTotal = flatFeeInfo.earnedAmount;

        // We need to know if there are any discounts applicable for this invoice
        const discounts = FinancialHelper.getDiscounts(invoice, taskTotal, expenseTotal, flatFeeInfo.earnedAmount, tasks)
            .filter(util.existsFilter);
        const discountTotal = MathUtils.sumByField(discounts, 'amount');

        // Get all payments and adjustments for this invoice
        const txns = transactions.filter(tr => ['DT', 'BA', 'PC', 'PT', 'FO'].includes(tr.txType) || (tr.txType === 'TT' && tr.sourceType === 'B'));

        const withdrawals = transactions.filter(tr => tr.txType === 'DT' &&
            (tr.invoiceId ? tr.invoiceId === invoice.id : true)
        );
        const withdrawalsTotal = MathUtils.sumByField(withdrawals, 'amount');

        // Need to ensure that client trust transactions are not counted as payments unless the matterIds match!
        const payments = txns.filter(tr => {
            if (!!invoice.finalizedOn && !invoice.transactionIds.includes(tr.id)) return false;
            // OWA-947: trust transfers do not affect billed or paid (John, 2026-10-01). A
            // finalized invoice computed here keeps what it was sent with.
            if (tr.txType === 'TT' && tr.sourceType === 'B') return invoice.invoiceStatus === 'F';
            if (invoice.invoiceStatus !== 'F' && !['PC', 'PT'].includes(tr.txType)) return false;
            return tr.txType !== 'BA' &&
                tr.txType !== 'DT' &&
                (util.arrayMatch(tr.matterIds, invoice.matterIds) ||
                    (tr.entityType === 'lead' && tr.entityId === invoice.leadId)
                )
        });

        const paymentTotal = payments.reduce((total, tr) => {
            if (tr.txType !== 'TT') return total + tr.amount;
            else return total - tr.amount
        }, 0);

        const refunds = transactions.filter(tr => tr.txType === 'RO');
        const refundTotal = MathUtils.sumByField(refunds, 'amount');

        const adjustments = txns.filter(tr => tr.txType === 'BA');
        const adjustmentTotal = MathUtils.sumByField(adjustments, 'amount');

        // Get new charges, which is the sum of labor and expenses, minus adjustments and discounts
        const currentInvoiceAmount = taskTotal + expenseTotal + flatFeeInfo.earnedAmount - discountTotal - adjustmentTotal + refundTotal;

        // Calculate past due amount (total all charges and payments excluding transactions in this invoice)
        const pastDue = FinancialHelper.calculatePastDue(invoice, transactions);

        // New Balance (principal only — tax and interest are separate additive fields)
        const currentBalance = pastDue + currentInvoiceAmount;

        // Tax computation
        const taxRate = OptionHelper.getOptionValueStatic(OptionHelper.INVOICE_TAX_RATE) as number || 0;
        const taxLabel = OptionHelper.getOptionValueStatic(OptionHelper.INVOICE_TAX_LABEL) as string || '';
        const taxTotal = calculateTaxTotal(expenses, taxRate);

        // Interest computation (on principal-only past-due balance from prior finalized invoices)
        let interestTotal = 0;
        const interestRate = OptionHelper.getOptionValueStatic(OptionHelper.INVOICE_INTEREST_RATE) as number || 0;
        if (interestRate > 0 && pastDue > 0 && invoice.matterIds?.length) {
            const interestMethod = OptionHelper.getOptionValueStatic(OptionHelper.INVOICE_INTEREST_METHOD) as string || InterestMethods.SIMPLE_MONTHLY;
            const interestGracePeriod = OptionHelper.getOptionValueStatic(OptionHelper.INVOICE_INTEREST_GRACE_PERIOD) as number || 0;

            const priorInvoices = FinancialHelper.getInvoices(invoice.matterIds[0])
                .filter(inv => inv.id !== invoice.id
                    && inv.invoiceStatus === 'F'
                    && inv.endDate <= invoice.endDate)
                .sort((a, b) => b.endDate.getTime() - a.endDate.getTime());

            const mostRecentDueDate = priorInvoices[0]?.dueDate;
            if (mostRecentDueDate) {
                const asOfDate = invoice.endDate ?? new Date();
                interestTotal = calculateInterest(
                    pastDue, mostRecentDueDate, asOfDate,
                    interestRate, interestMethod, interestGracePeriod
                );
            }
        }

        // Past due stays principal-only for interest calculation. A draft also owes
        // the prior invoices' frozen tax and interest; historical documents keep
        // their original figures, including where the old computation omitted them.
        const priorTaxAndInterest = invoice.invoiceStatus === 'F' ? 0 : MathUtils.sum(
            FinancialHelper.getInvoices(invoice.matterIds[0])
                .filter(inv => inv.id !== invoice.id && inv.invoiceStatus === 'F' && inv.endDate <= DateUtil.endOfDay(invoice.endDate))
                .map(inv => FinancialHelper.getInvoiceInfo(inv))
                .filter(util.existsFilter)
                .map(ii => (ii.taxTotal ?? 0) + (ii.interestTotal ?? 0)));
        // Amount due (principal balance + carried and current tax/interest - payments)
        const amountDue = currentBalance + priorTaxAndInterest + taxTotal + interestTotal - paymentTotal;

        // Is this the first invoice for this matter?
        const isFirstInvoice = FinancialHelper.isFirstInvoice(invoice);

        // Return raw scale-4 values; consumers round at the display boundary
        // (OWA-451). Storing pre-rounded values poisoned downstream sums.
        return {
            invoiceId: invoice.id,
            invoice: invoice,
            tasks,
            expenses,
            transactions,
            taskTotal,
            expenseTotal,
            transactionTotal,
            discounts,
            discountTotal,
            flatFeeInfo,
            flatFeeTotal,
            payments,
            paymentTotal,
            adjustments,
            adjustmentTotal,
            refunds,
            refundTotal,
            withdrawals,
            withdrawalsTotal,
            currentInvoiceAmount,
            currentBalance,
            amountDue,
            pastDue,
            isFirstInvoice,
            taxTotal,
            interestTotal,
            taxLabel,
        };
    };

    /** Frozen task lines for invoice views. Empty drafts and missing snapshots have no lines. */
    static getFinalizedInvoiceTaskLines = (invoice: Invoice): Task[] => {
        const fv = invoice.finalValues as FinalValues | null;
        if (invoice.invoiceStatus !== 'F' || !fv?.tasks) return [];
        return fv.tasks.map(ft => ({
            id: ft.taskId,
            taskId: ft.taskId,
            title: ft.title,
            invoiceNotes: ft.invoiceNotes,
            completionDate: ft.completionDate ? new Date(ft.completionDate) : null,
            assignedTo: ft.assignedToId,
            createdBy: ft.assignedToId,
            elapsedTime: ft.elapsedTime,
            associatedRate: ft.rate,
            hideFromInvoice: ft.hideFromInvoice,
            noCharge: ft.noCharge,
            costOverride: ft.overrideCost,
            isCostOverridden: ft.overrideCost != null,
            hoursOverride: ft.overrideHours,
            rateOverride: ft.overrideRate,
            flatFeeBucket: ft.flatFeeBucket,
            // An invoice finalized before OWA-1802 has no frozen name or effective rate on the line: take the
            // name from the snapshot's own fee list when it has the fee, and the plain rate otherwise.
            _finalFlatFeeName: ft.flatFeeBucketName ?? fv.flatFees?.find(ff => ff.flatFeeId === ft.flatFeeBucket)?.name ?? null,
            _finalRate: ft.effectiveRate ?? ft.rate,
            activityCode: ft.activityCode,
            notification: ft.notification,
            billable: true,
            // Pre-resolved display fields (for consumers that check finalValues)
            _finalAssignedToName: ft.assignedToName,
            _finalAssignedToInitials: ft.assignedToInitials,
            _finalCost: ft.cost,
            _finalHours: ft.hours,
        })) as unknown as Task[];
    };

    /** Build invoice figures and proxies from the frozen snapshot. */
    private static _buildInvoiceInfoFromFinalValues = (invoice: Invoice, fv: FinalValues): InvoiceInfo => {
        const tasks = FinancialHelper.getFinalizedInvoiceTaskLines(invoice);

        // Build expense proxies
        const expenses = fv.expenses.map(fe => ({
            id: fe.expenseId,
            expenseId: fe.expenseId,
            description: fe.description,
            expenseDate: fe.expenseDate ? new Date(fe.expenseDate) : null,
            cost: fe.cost,
            quantity: fe.quantity,
            payee: null, // No live lookup needed — payeeName is pre-resolved
            reimbursable: fe.reimbursable,
            taxable: fe.taxable,
            expenseType: fe.expenseType,
            _finalPayeeName: fe.payeeName,
            _finalTotal: fe.total,
        })) as unknown as Expense[];

        // Build transaction proxies
        function txProxy(ft: typeof fv.payments[0]): Transaction {
            return {
                id: ft.transactionId,
                transactionId: ft.transactionId,
                txType: ft.txType,
                txDate: ft.txDate ? new Date(ft.txDate) : null,
                amount: ft.amount,
                notes: ft.notes,
                sourceType: ft.sourceType,
                source: ft.source,
                status: 'C',
                voidedDate: null,
                refusedDate: null,
                _finalDescription: ft.description,
            } as unknown as Transaction;
        }

        const payments = fv.payments.map(txProxy);
        const adjustments = fv.adjustments.map(txProxy);
        const withdrawals = fv.withdrawals.map(txProxy);
        const refunds = fv.refunds.map(txProxy);
        const allTransactions = [...payments, ...adjustments, ...withdrawals, ...refunds];

        // Build discount proxies
        const discounts = fv.discounts.map(fd => ({
            id: fd.discountId,
            discountId: fd.discountId,
            reason: fd.reason,
            amount: fd.amount,
            method: fd.method,
            percentage: fd.percentage,
            simple: fd.simple,
            applyToLabor: fd.applyToLabor,
            applyToExpenses: fd.applyToExpenses,
            laborAmount: fd.laborAmount,
            expenseAmount: fd.expenseAmount,
            _finalReasonDisplay: fd.reasonDisplay,
        })) as unknown as Discount[];

        // Build flatFeeInfo
        const flatFees = fv.flatFees.map(ff => ({
            id: ff.flatFeeId,
            flatFeeId: ff.flatFeeId,
            name: ff.name,
            description: ff.description,
            amount: ff.amount,
            earnedThisPeriod: ff.earnedThisPeriod,
        })) as unknown as FlatFee[];

        const flatFeeMilestones = fv.flatFeeMilestones.map(ffm => ({
            id: ffm.flatFeeMilestoneId,
            flatFeeMilestoneId: ffm.flatFeeMilestoneId,
            name: ffm.name,
            description: ffm.description,
            amount: ffm.amount,
            percentage: ffm.percentage,
            earnedThisPeriod: ffm.earnedThisPeriod,
        })) as unknown as FlatFeeMilestone[];

        return {
            invoiceId: invoice.id,
            invoice,
            tasks,
            expenses,
            transactions: allTransactions,
            taskTotal: fv.taskTotal,
            expenseTotal: fv.expenseTotal,
            transactionTotal: fv.transactionTotal,
            discounts,
            discountTotal: fv.discountTotal,
            flatFeeInfo: {
                earnedAmount: fv.flatFeeTotal,
                flatFees,
                flatFeeMilestones,
            },
            flatFeeTotal: fv.flatFeeTotal,
            payments,
            paymentTotal: fv.paymentTotal,
            adjustments,
            adjustmentTotal: fv.adjustmentTotal,
            refunds,
            refundTotal: fv.refundTotal,
            withdrawals,
            withdrawalsTotal: fv.withdrawalsTotal,
            currentInvoiceAmount: fv.currentInvoiceAmount,
            currentBalance: fv.currentBalance,
            amountDue: fv.amountDue,
            pastDue: fv.pastDue,
            isFirstInvoice: fv.isFirstInvoice,
            taxTotal: fv.taxTotal ?? 0,
            interestTotal: fv.interestTotal ?? 0,
            taxLabel: fv.taxLabel ?? '',
        };
    };

    /**
     * Is this the first invoice for these clients/matters?
     *
     * Fetches an array of invoices, not including this one:
     *   1. Must match all clients, period.
     *   2. If ignoreMatters is not true, then all matters must also match.
     *   3. The issueDate for candidate invoices must be less than or equal to the issueDate for the specified invoice.
     *
     * If there are any invoices matching those criteria, then this is NOT the first invoice, and we return false.
     * If no matching invoices are found, then this is the first invoice, and we return true.
     *
     * @param {Invoice} invoice
     * @param {boolean} [ignoreMatters]
     * @returns {boolean} - is this the first invoice for this client matter?
     */
    static isFirstInvoice = (invoice: Invoice, ignoreMatters?: boolean): boolean => {
        return store.getState().invoices.filter(inv => {
            // A deleted invoice never counts as an earlier one: deleting it releases its
            // client/matter's first-invoice slot, and a retained deleted record (OWA-68) must not
            // suppress the starting trust/balance entries the next real invoice needs.
            if (inv.status === 'D') return false;
            if (!!inv.leadId || (inv.id === invoice.id)) return false;

            const clientsMatch = util.arrayMatch(inv.clientIds, invoice.clientIds);
            if (!clientsMatch) return false;

            if (!ignoreMatters) {
                const mattersMatch = util.arrayMatch(inv.matterIds, invoice.matterIds);
                if (!mattersMatch) return false;
            }

            return inv.issueDate <= invoice.issueDate;
        }).length === 0;
    };

    /**
     * Get all discounts for an invoice. Passing taskTotal or expenseTotal will save some computation.
     *
     * @param {Invoice} invoice
     * @param {number} [taskTotal]
     * @param {number} [expenseTotal]
     * @param {number} [flatFeeTotal]
     * @param {Task[]} [tasksForInvoice]
     * @return {Discount[]}
     */
    static getDiscounts = (invoice: Invoice, taskTotal: number = 0, expenseTotal: number = 0, flatFeeTotal: number = 0, tasksForInvoice: Task[]): Discount[] => {
        if (isNaN(taskTotal) || isNaN(expenseTotal) || isNaN(flatFeeTotal)) {
            const tasks = tasksForInvoice || FinancialHelper.getTasksForInvoice(invoice);
            const expenses = FinancialHelper.getExpensesForInvoice(invoice);
            const flatFeeInfo = FinancialHelper.getFlatFeesForInvoice(invoice);

            // Calculate simple totals
            taskTotal = taskTotal || MathUtils.sum(tasks.map(t => getCost(t, invoice)));
            expenseTotal = MathUtils.sum(expenses.map(e => e.quantity * e.cost));
            flatFeeTotal = flatFeeInfo.earnedAmount;
        }

        const computeTotal = (discount: Discount) => {
            let total = 0, laborAmount = 0, expenseAmount = 0;

            if (discount.method === 'S') {
                total = discount.simple ?? 0;
                laborAmount = Math.max(total, taskTotal + flatFeeTotal);
                expenseAmount = total - laborAmount;
            }
            else {
                // Use the percentage at full precision — rounding it to 2dp
                // before multiplying loses sub-percent precision (e.g.
                // 15.005% × $1000 should be $150.05, not $150.00). OWA-451.
                const pct = (discount.percentage ?? 0) / 100.0;
                if (discount.applyToLabor) {
                    laborAmount += (flatFeeTotal + taskTotal) * pct;
                    total += (flatFeeTotal + taskTotal) * pct;
                }
                if (discount.applyToExpenses) {
                    expenseAmount += expenseTotal * pct;
                    total += expenseTotal * pct;
                }
            }

            // Return scale-4 — display-rounded by consumers.
            return {
                total,
                laborAmount,
                expenseAmount,
            };
        };

        return store.getState().discounts
            .getMappingFor('invoiceId', invoice.id)
            .map(discount => {
                const discountClone = discount.clone();

                const {total, laborAmount, expenseAmount} = computeTotal(discountClone)
                discountClone.amount = total;
                discountClone.laborAmount = laborAmount;
                discountClone.expenseAmount = expenseAmount;

                return discountClone;
            });
    }

    /**
     * Get the past due amount for this invoice, which is the sum of previous amounts due minus any payments.
     *
     * @param {Invoice} invoice
     * @param {Transaction[]} excludeTransactions - these are the transactions we will not include in the calculation
     * @return {number}
     */
    static calculatePastDue = (invoice: Invoice, excludeTransactions: Transaction[]): number => {
        // Always zero for a lead (CSM)
        if (invoice.leadId) return 0;

        // If the invoice has an end date, use it, otherwise use today
        const endDate = invoice.endDate ? DateUtil.endOfDay(invoice.endDate) : new Date();

        // The amount due is based on all the past charges, discounts and payments, UP TO THIS INVOICE.
        // That means we need to find all prior invoices, get the total of currentInvoiceAmount and payments, and add
        // that to any SA transaction (starting account balance) if this is the first invoice.
        const excludeTransactionIds = excludeTransactions.filter(tx => tx.txType !== 'SA').map(tx => tx.id);

        const transactions = store.getState()
            .transactions
            .getMappingFor('matterId', invoice.leadId ? invoice.leadId : invoice.matterIds[0])
            .filter(tr => {
                if (tr.voidedDate || tr.refusedDate || tr.status === 'D' || excludeTransactionIds.includes(tr.id)) return false;

                // We match transactions by matters and clients
                const matterIds = invoice.leadId ? [invoice.leadId] : invoice.matterIds;
                return matterIds.includes(tr.source as uuid) || matterIds.includes(tr.target as uuid) ||
                    (tr.sourceType === 'G' && tr.targetType === 'B' && util.arrayMatch(tr.matterIds, matterIds)) ||
                    ((tr.entityType === 'lead' && tr.entityId === invoice.id) && (tr.txDate <= endDate));
            })

        // console.log(`Found ${transactions.length} transactions for invoice ${invoice.invoiceNumber}`);
        // console.table(transactions.map(tr => ({
        //     date: tr.txDate.toISOString(),
        //     type: tr.txType,
        //     sourceType: tr.sourceType,
        //     targetType: tr.targetType,
        //     amount: tr.amount,
        //     voided: !!(tr.voidedDate || tr.refusedDate), status: tr.status,
        //     valid: FinancialHelper.transactionFailureReason(tr) === null
        // })));

        // Use any SA transaction as the starting balance
        const startingBalance = transactions.find(tr => tr.txType === 'SA')?.amount ?? 0;

        // We need the actual invoices so that we can get the discounts applied on each.
        // Note that we often leave the startDate null to pick up stragglers, so we cannot depend on that
        // date being non-null.  Instead, to get rid of invoices AFTER this invoice, we need to look for invoices
        // with an end date AFTER this invoice's end date.
        const invoices = FinancialHelper.getInvoices(invoice.matterIds[0])
            .filter(inv => (inv.id !== invoice.id) &&
                (inv.invoiceStatus === InvoiceStatus.Final) &&
                // Include any invoices up until the end of the end date
                (inv.endDate <= DateUtil.endOfDay(invoice.endDate)));

        const invoiceMap = new Map<string, InvoiceInfo | null>();

        invoices.filter(util.existsFilter)
            .forEach(inv => {
                const ii = FinancialHelper.getInvoiceInfo(inv);
                if (ii) invoiceMap.set(inv.invoiceNumber, ii);
                else {
                    if (store.getState().misc.restoreStatus === RestoreStatus.Restored)
                        console.log(`No invoice info found for invoice ${inv.id} (${inv.invoiceNumber}) in calculatePastDue`);

                    invoiceMap.set(inv.invoiceNumber, null);
                }
            });

        const iis = invoices.filter(util.existsFilter) // Get rid of any Invoices that are null in the invoice array
            .map(inv => invoiceMap.get(inv.invoiceNumber)) // remap Invoice to InvoiceInfo
            .filter(util.existsFilter); // Get rid of entries that failed to retrieve InvoiceInfo

        // Only process if there was InvoiceInfo
        if (iis) {
            const labor = MathUtils.sum(iis.map(ii => ii.taskTotal));
            const expenses = MathUtils.sum(iis.map(ii => ii.expenseTotal));
            const flatFees = MathUtils.sum(iis.map(ii => ii.flatFeeInfo.earnedAmount));
            const discounts = MathUtils.sum(iis.map(ii => ii.discountTotal));
            // OWA-947: on a draft, a transaction listed by two finalized invoices (a failed
            // back-pointer save, OWA-946 item 5) counts once, and refunds count. A finalized
            // invoice computed here (no frozen snapshot) keeps the figures it was sent with.
            const keepsSentFigures = invoice.invoiceStatus === 'F';
            const priorRows = iis.map(ii => {
                const linked = keepsSentFigures ? [] : store.getState().transactions.getMappingFor('invoiceId', ii.invoice.id);
                return {
                    ii,
                    payments: ii.payments.length ? ii.payments : linked.filter(tx => ['PC', 'PT', 'FO', 'TT'].includes(tx.txType)),
                    adjustments: ii.adjustments.length ? ii.adjustments : linked.filter(tx => tx.txType === 'BA'),
                    refunds: ii.refunds.length ? ii.refunds : linked.filter(tx => tx.txType === 'RO'),
                };
            });
            const once = (txs: Transaction[]) => keepsSentFigures ? txs : txs
                .filter((tx, i) => txs.findIndex(t => t.id === tx.id) === i)
                .map(tx => store.getState().transactions.get(tx.id, false))
                .filter(util.existsFilter)
                .filter(tx => tx.status !== 'D' && !tx.voidedDate && !tx.refusedDate);
            const adjustments = keepsSentFigures
                ? MathUtils.sum(iis.map(ii => ii.adjustmentTotal))
                : MathUtils.sumByField(once(priorRows.flatMap(row => row.adjustments)), 'amount');
            const refunds = keepsSentFigures ? 0 : MathUtils.sumByField(once(priorRows.flatMap(row => row.refunds)), 'amount');
            const payments = once(priorRows.flatMap(row => row.payments))
                .filter(tx => keepsSentFigures || ['PC', 'PT'].includes(tx.txType));
            // Scalar-only legacy snapshots may have no identities to recover.
            // Match the account allocator's unexplained settlement, keeping it
            // undated. Known rows (including invalidated ones) are not residuals.
            const undatedSettlement = keepsSentFigures ? 0 : MathUtils.sum(priorRows.map(row =>
                row.ii.paymentTotal - MathUtils.sum(row.payments.map(tx => tx.txType === 'TT' ? -tx.amount : tx.amount)) +
                row.ii.adjustmentTotal - MathUtils.sumByField(row.adjustments, 'amount') -
                row.ii.refundTotal + MathUtils.sumByField(row.refunds, 'amount')));

            // Paid is a negative number, as it lowers the amountDue.
            const paid = MathUtils.sum(payments.map(tx => {
                // Otherwise we have to check to see if the transaction is positive or negative in terms of account balance
                return (FinancialHelper.isNegative(tx, [...invoice.clientIds, ...invoice.matterIds]) ? -1 : 1) * tx.amount;
            }));

            return startingBalance + labor + flatFees + expenses - discounts - adjustments + refunds + paid - undatedSettlement;
        }
        else {
            console.warn('iis is null');
            captureException(new Error('iis is null'), {extra: [], tags: ['FinancialHelper']});
            return 0;
        }
    };

    /**
     * Get invoices related to a specific (set of) matter(s)
     * @param {uuid} matterId
     * @param {'asc'|'desc'} [sortOrder]
     * @return {Invoice[]}
     */
    static getInvoices = (matterId: uuid, sortOrder: 'asc' | 'desc' = 'asc'): Invoice[] => {
        const invoices = store.getState()
            .invoices
            .getMappingFor('matterId', matterId);

        if (sortOrder === 'asc') return invoices.sort((a, b) => dateAsNumber(a.issueDate) - dateAsNumber(b.issueDate));
        else return invoices.sort((a, b) => dateAsNumber(b.issueDate) - dateAsNumber(a.issueDate));
    };

    /**
     * Find invoices for a lead
     *
     * @param {uuid} leadId
     * @returns {Invoice[]}
     */
    static getInvoicesForLead = (leadId: uuid): Invoice[] => {
        return store.getState().invoices
            .filter(invoice => invoice.status !== 'D' && invoice.leadId === leadId)
            .sort((a, b) => dateAsNumber(a.issueDate) - dateAsNumber(b.issueDate));
    };

    /**
     * Get the latest invoice for a given matter ID.
     *
     * @param {uuid} matterId
     * @returns {Invoice}
     */
    static getLatestInvoice = (matterId: uuid): Invoice => {
        return FinancialHelper.getInvoices(matterId, 'desc')[0];
    }

    /* Function for calculating billed and paid */

    /**
     * Find payments to a matter that have been made but not attached to an invoice
     *
     * @param {uuid[]} matterIds
     * @return {Transaction[]}
     */
    /**
     * OWA-551: every uninvoiced (not yet on any invoice) balance-affecting
     * transaction for these matters — payments (PC/PT), balance adjustments (BA)
     * and refunds (RO) — i.e. activity since the last finalize. Callers sign each
     * via {@link #isNegative} (a payment/credit reduces what's owed, a refund
     * increases it) and allocate oldest-invoice-first. Trust movements (FR/ST/TT)
     * are intentionally excluded — they do not affect the invoice receivable.
     */
    static getUnInvoicedBalanceTransactions = (matterIds: uuid[]): Transaction[] => {
        return store.getState()
            .transactions
            .getIndex('notDeleted')
            .filter(tx => !(tx.voidedDate || tx.refusedDate) &&
                (util.arrayMatch(matterIds, tx.matterIds) || matterIds.includes(tx.source as uuid) || matterIds.includes(tx.target as uuid)) &&
                !tx.invoiceId &&
                ['PC', 'PT', 'BA', 'RO'].includes(tx.txType)
            );
    };

    /**
     * OWA-947: one read model for cash, noncash settlement and current receivables.
     * Charges come from the invoice's own period, never its carry-forward balance.
     * Claim transactions on the earliest finalized snapshot first, then drafts and
     * live rows. A snapshot's missing itemization stays undated, not fabricated income.
     * Receipts and credits fill remaining receivables in transaction-date order.
     * Refunds reverse cash on the newest paid receivable first. Reports filter the
     * resulting cash events by receipt date AFTER allocation, not the input history.
     * This function never changes an invoice or a transaction.
     */
    static getMoneyAllocation = (matterIds: uuid[], invoiceInfos?: InvoiceInfo[], matters?: Matter[]): MoneyAllocation => {
        const state = store.getState();
        // Upstream totals use JS arithmetic and unrounded interest. Normalize
        // external inputs once; all allocation arithmetic stays exact at scale 4.
        const money = (amount: number) => Math.round(amount * 10000) / 10000;
        const sum = (values: number[]) => moneyCheckedSum(new Float64Array(values));
        const belongs = (tx: Transaction) => (tx.matterIds ?? []).some(mid => matterIds.includes(mid)) ||
            matterIds.includes(tx.source ?? '') || matterIds.includes(tx.target ?? '') ||
            (tx.entityType === 'lead' && matterIds.includes(tx.entityId));
        const valid = (tx: Transaction) => tx.status !== 'D' && !tx.voidedDate && !tx.refusedDate;
        const transactions = state.transactions.getIndex('notDeleted').filter(tx => valid(tx) && belongs(tx));
        matters ??= matterIds.map(mid => state.matters.get(mid, false)).filter(util.existsFilter);
        const startingBilled = sum([
            ...matters.map(m => money(m.startingBilled || 0)),
            ...transactions.filter(tx => tx.txType === 'SA').map(tx => money(tx.amount)),
        ]);
        let undatedSettled = sum(matters.map(m => money(m.startingCollected || 0)));
        const selected = (invoice: Invoice) => invoice.status !== 'D' &&
            (invoice.leadId ? matterIds.includes(invoice.leadId) :
                invoice.matterIds.length > 0 && invoice.matterIds.every(mid => matterIds.includes(mid)));
        const infos = (invoiceInfos ?? state.invoices.filter(selected)
            .map(invoice => FinancialHelper.getInvoiceInfo(invoice)).filter(util.existsFilter))
            .filter(ii => selected(ii.invoice))
            .sort((a, b) => dateAsNumber(a.invoice.issueDate) - dateAsNumber(b.invoice.issueDate) ||
                a.invoice.invoiceNumber.localeCompare(b.invoice.invoiceNumber) || a.invoice.id.localeCompare(b.invoice.id));
        // Selecting several cases for a report must not let one client's cash pay
        // another client's invoice. Multi-case invoices remain a legacy-only limit.
        if (matterIds.length > 1 && infos.every(ii => !!ii.invoice.leadId || ii.invoice.matterIds.length === 1)) {
            const allocations = [...new Set(matterIds)].map(mid => FinancialHelper.getMoneyAllocation([mid],
                infos.filter(ii => ii.invoice.leadId === mid || ii.invoice.matterIds.includes(mid)), matters.filter(m => m.id === mid)));
            return {
                invoices: infos.map(ii => allocations.flatMap(a => a.invoices).find(row => row.invoiceInfo.invoice.id === ii.invoice.id)!),
                cashEvents: allocations.flatMap(a => a.cashEvents).sort((a, b) => dateAsNumber(a.txDate ?? undefined) - dateAsNumber(b.txDate ?? undefined) || a.transactionId.localeCompare(b.transactionId)),
                credits: allocations.flatMap(a => a.credits),
                startingBilled: sum(allocations.map(a => a.startingBilled)), undatedSettled: sum(allocations.map(a => a.undatedSettled)),
                finalizedBilled: sum(allocations.map(a => a.finalizedBilled)), totalBilled: sum(allocations.map(a => a.totalBilled)),
                totalSettled: sum(allocations.map(a => a.totalSettled)), outstanding: sum(allocations.map(a => a.outstanding)),
                unappliedCash: sum(allocations.map(a => a.unappliedCash)),
            };
        }
        const credits: MoneyAllocation['credits'] = [];
        const cashEvents: CashAllocationEvent[] = [];
        const claimed = new Set<uuid>();
        const txDate = (tx: Transaction): Date | null => tx.txDate && Number.isFinite(dateAsNumber(tx.txDate)) ? tx.txDate : null;
        const collect = (tx: Transaction) => {
            if (claimed.has(tx.id)) return;
            claimed.add(tx.id);
            const live = state.transactions.get(tx.id, false);
            // A known transaction removed from the live ledger must not be
            // resurrected from a legal document's immutable payment snapshot.
            if (!live || !valid(live) || !belongs(live)) return;
            const current = live;
            if (current.txType === 'BA') credits.push({transactionId: current.id, txDate: txDate(current), amount: money(current.amount)});
            else if (['PC', 'PT', 'RO'].includes(current.txType)) cashEvents.push({
                transactionId: current.id, txDate: txDate(current), amount: money(current.txType === 'RO' ? -current.amount : current.amount),
                allocations: [], unapplied: 0,
            });
        };
        const finalized = infos.filter(ii => ii.invoice.invoiceStatus === 'F')
            .sort((a, b) => dateAsNumber(a.invoice.finalizedOn ?? a.invoice.issueDate) - dateAsNumber(b.invoice.finalizedOn ?? b.invoice.issueDate) ||
                dateAsNumber(a.invoice.issueDate) - dateAsNumber(b.invoice.issueDate) ||
                a.invoice.invoiceNumber.localeCompare(b.invoice.invoiceNumber) || a.invoice.id.localeCompare(b.invoice.id));
        [...finalized, ...infos.filter(ii => ii.invoice.invoiceStatus !== 'F')].forEach(ii => {
            // Older snapshots sometimes have totals without transaction rows. Recover
            // real dated rows when available; keep only the unexplained remainder undated.
            const linked = state.transactions.getMappingFor('invoiceId', ii.invoice.id).filter(belongs);
            const payments = ii.payments.length ? ii.payments : linked.filter(tx => ['PC', 'PT', 'FO', 'TT'].includes(tx.txType));
            const adjustments = ii.adjustments.length ? ii.adjustments : linked.filter(tx => tx.txType === 'BA');
            const refunds = ii.refunds.length ? ii.refunds : linked.filter(tx => tx.txType === 'RO');
            if (ii.invoice.invoiceStatus === 'F') {
                undatedSettled = moneyCheckedAdd(undatedSettled, moneyCheckedSubtract(money(ii.paymentTotal),
                    sum(payments.map(tx => money(tx.txType === 'TT' ? -tx.amount : tx.amount)))));
                undatedSettled = moneyCheckedSubtract(undatedSettled, moneyCheckedSubtract(money(ii.refundTotal), sum(refunds.map(tx => money(tx.amount)))));
                const unitemizedCredit = moneyCheckedSubtract(money(ii.adjustmentTotal), sum(adjustments.map(tx => money(tx.amount))));
                if (unitemizedCredit) credits.push({transactionId: null, txDate: null, amount: unitemizedCredit});
            }
            [...payments, ...adjustments, ...refunds].forEach(collect);
        });
        transactions.forEach(collect);
        const invoices: InvoiceMoneyBalance[] = infos.map(ii => {
            const principal = moneyCheckedSubtract(moneyCheckedAdd(money(ii.currentInvoiceAmount), money(ii.adjustmentTotal)), money(ii.refundTotal));
            const tax = money(ii.taxTotal ?? 0);
            const interest = money(ii.interestTotal ?? 0);
            const discount = money(ii.discountTotal ?? 0);
            const billed = sum([principal, tax, interest]);
            return {invoiceInfo: ii, principal, discount, tax, interest, billed, cash: 0, credit: 0, undated: 0, outstanding: Math.max(0, billed)};
        });
        const creditTotal = sum(credits.map(e => e.amount));
        const opening = {
            billed: sum([Math.max(0, startingBilled), Math.max(0, -undatedSettled)]),
            cash: 0, credit: 0, undated: 0, outstanding: 0,
        };
        opening.outstanding = opening.billed;
        const rows = [opening, ...invoices];
        const apply = (amount: number, kind: 'cash' | 'credit' | 'undated', event?: CashAllocationEvent) => {
            let remaining = amount;
            rows.forEach((row, index) => {
                const applied = Math.min(row.outstanding, remaining);
                if (applied <= 0) return;
                row[kind] = moneyCheckedAdd(row[kind], applied);
                row.outstanding = moneyCheckedSubtract(row.outstanding, applied);
                remaining = moneyCheckedSubtract(remaining, applied);
                event?.allocations.push({invoiceId: index === 0 ? null : invoices[index - 1].invoiceInfo.invoice.id, amount: applied});
            });
            return remaining;
        };
        apply(Math.max(0, undatedSettled), 'undated');
        let unappliedCredit = apply(Math.max(0, -startingBilled), 'credit');
        let unappliedCash = 0;
        cashEvents.sort((a, b) => dateAsNumber(a.txDate ?? undefined) - dateAsNumber(b.txDate ?? undefined) ||
            a.transactionId.localeCompare(b.transactionId));
        const history = [
            ...cashEvents.map(event => ({kind: 'cash' as const, event})),
            ...credits.map(event => ({kind: 'credit' as const, event})),
        ].sort((a, b) => dateAsNumber(a.event.txDate ?? undefined) - dateAsNumber(b.event.txDate ?? undefined) ||
            String(a.event.transactionId ?? '').localeCompare(String(b.event.transactionId ?? '')));
        history.forEach(item => {
            if (item.kind === 'credit') {
                if (item.event.amount >= 0) unappliedCredit = moneyCheckedAdd(unappliedCredit, apply(item.event.amount, 'credit'));
                else {
                    let debit = -item.event.amount;
                    const fromExcess = Math.min(debit, unappliedCredit);
                    unappliedCredit = moneyCheckedSubtract(unappliedCredit, fromExcess);
                    debit = moneyCheckedSubtract(debit, fromExcess);
                    for (let index = rows.length - 1; index >= 0 && debit > 0; index--) {
                        const reversed = Math.min(Math.max(0, rows[index].credit), debit);
                        rows[index].credit = moneyCheckedSubtract(rows[index].credit, reversed);
                        rows[index].outstanding = moneyCheckedAdd(rows[index].outstanding, reversed);
                        debit = moneyCheckedSubtract(debit, reversed);
                    }
                    if (debit) {
                        const row = invoices[0] ?? opening;
                        row.credit = moneyCheckedSubtract(row.credit, debit);
                        row.outstanding = moneyCheckedAdd(row.outstanding, debit);
                    }
                }
                return;
            }
            const event = item.event;
            if (event.amount >= 0) {
                const recoveredDebit = Math.min(event.amount, Math.max(0, -unappliedCash));
                unappliedCash = moneyCheckedAdd(unappliedCash, recoveredDebit);
                unappliedCash = moneyCheckedAdd(unappliedCash, apply(moneyCheckedSubtract(event.amount, recoveredDebit), 'cash', event));
            }
            else {
                let remaining = -event.amount;
                const fromExcess = Math.min(remaining, Math.max(0, unappliedCash));
                unappliedCash = moneyCheckedSubtract(unappliedCash, fromExcess);
                remaining = moneyCheckedSubtract(remaining, fromExcess);
                // Reverse actual cash first; legacy undated paid amounts can also
                // explain a refund, but a write-off must never become cash income.
                for (const kind of ['cash', 'undated'] as const) {
                    for (let index = rows.length - 1; index >= 0 && remaining > 0; index--) {
                        const row = rows[index];
                        const reversed = Math.min(row[kind], remaining);
                        if (!reversed) continue;
                        row[kind] = moneyCheckedSubtract(row[kind], reversed);
                        row.outstanding = moneyCheckedAdd(row.outstanding, reversed);
                        remaining = moneyCheckedSubtract(remaining, reversed);
                        event.allocations.push({invoiceId: index === 0 ? null : invoices[index - 1].invoiceInfo.invoice.id, amount: -reversed});
                    }
                }
                unappliedCash = moneyCheckedSubtract(unappliedCash, remaining);
                // A later credit may have settled a newer invoice or remained
                // unapplied. Let it cover the reopened oldest debt, preserving
                // the receipt/refund allocations that actually occurred.
                const availableCredit = sum([unappliedCredit, ...rows.map(row => Math.max(0, row.credit))]);
                rows.forEach(row => {
                    const released = Math.max(0, row.credit);
                    row.credit = moneyCheckedSubtract(row.credit, released);
                    row.outstanding = moneyCheckedAdd(row.outstanding, released);
                });
                unappliedCredit = apply(availableCredit, 'credit');
            }
            event.unapplied = moneyCheckedSubtract(event.amount, sum(event.allocations.map(a => a.amount)));
        });
        const totalBilled = sum([startingBilled, ...invoices.map(row => row.billed)]);
        const totalSettled = sum([undatedSettled, creditTotal, ...cashEvents.map(e => e.amount)]);
        return {
            invoices, cashEvents, credits, startingBilled, undatedSettled,
            finalizedBilled: sum(invoices.filter(row => row.invoiceInfo.invoice.invoiceStatus === 'F').map(row => row.billed)),
            totalBilled, totalSettled, outstanding: moneyCheckedSubtract(totalBilled, totalSettled), unappliedCash,
        };
    };

    /**
     * Get an overall billed and paid for each invoice related to the given matters.
     *
     * @param {uuid[]} matterIds
     * @param {Array.<InvoiceInfo>} [invoiceInfos] - if provided, use the invoiceInfos provided
     * @return {Array.<BilledAndPaidInvoice>}
     */
    static getBilledAndPaidForMatters(matterIds: uuid[], invoiceInfos?: Array<InvoiceInfo>): Array<BilledAndPaidInvoice> {
        // Compatibility name: collected here means receivable settlement, including
        // noncash credits. Cash reports use getMoneyAllocation().cashEvents instead.
        return FinancialHelper.getMoneyAllocation(matterIds, invoiceInfos).invoices.map(row => {
            const ii = row.invoiceInfo;
            const billed = row.billed;
            const collected = moneyCheckedSubtract(billed, row.outstanding);

            return {
                invoiceNumber: ii.invoice.invoiceNumber,
                invoiceId: ii.invoice.id,
                startDate: ii.invoice.startDate,
                endDate: ii.invoice.endDate,
                billed,
                collected,
                expensesTotal: ii.expenseTotal,
                paid: billed === collected,
                finalized: ii.invoice.invoiceStatus === 'F',
                taskIds: ii.tasks
                    .filter(util.existsFilter)
                    .sort((a, b) => dateAsNumber(a.completionDate ?? undefined) - dateAsNumber(b.completionDate ?? undefined))
                    .map(t => t.id),
                discounts: ii.discounts
            };
        });
    }


    /**
     * Figure out the portion of this flat fee that has been earned.
     *
     * @param {Task} t
     * @returns {number}
     */
    static getFlatFeePortion = (t: Task): number => {
        // First, has this flat fee been earned? Check both flatFeeBucket and flatFeeMilestoneBucket.

        let ff = store.getState().flatFees.get(t.flatFeeBucket ?? '', false);

        const ffm = t.flatFeeMilestoneBucket
                    ? store.getState().flatFeeMilestones.get(t.flatFeeMilestoneBucket, false)
                    : null;

        const type = ffm ? 'milestone' : 'flatFee';
        const flatFee = ffm || ff;

        if (!flatFee) {
            console.warn(`Task ${t.id} has a deleted flat fee or flat fee milestone.`);
            return 0;
        }

        if (flatFee.earned) {
            /**
             * Now see how many total task hours have been allotted to this bucket.
             */
            const tasks = type === 'flatFee'
                          ? store.getState().tasks.getMappingFor('flatFeeBucket', flatFee.id)
                          : store.getState().tasks.getMappingFor('flatFeeMilestoneBucket', flatFee.id);

            const totalElapsed = tasks.reduce((total, task) => total + task.elapsedTime, 0);
            const portion = totalElapsed === 0 ? 0 : t.elapsedTime / totalElapsed;

            return Number((flatFee.amount * portion).toFixed(2));
        }
        else return 0;
    }

}

export default FinancialHelper;
